/**
 * Typed wrapper around the ui-server REST API. Re-uses TanStack Query for caching
 * + mutations, but the actual HTTP work happens here so the shape stays in one place.
 *
 * All requests go through /api/* which Vite's dev server proxies to
 * http://127.0.0.1:4400 (see vite.config.ts).
 */

export type Category =
  | "llm"
  | "search"
  | "scraping"
  | "media"
  | "email"
  | "messaging"
  | "social"
  | "cms"
  | "infra";

export interface FieldView {
  key: string;
  label: string;
  description?: string;
  required: boolean;
  sensitive: boolean;
  placeholder?: string;
  validator?: "url" | "email" | "number";
  /** Masked value (when sensitive) or plaintext (when not) or undefined when unset. */
  value?: string;
  /** True when a non-empty value exists in .env. */
  set: boolean;
  /** When present, the field renders as a select with these choices instead of a text input. */
  options?: Array<{ value: string; label: string; description?: string }>;
}

export interface IntegrationActionView {
  id: string;
  label: string;
  description?: string;
  disabledHint?: string;
  requiresFields: string[];
  /** True when every requiresFields key has a non-empty saved value. */
  ready: boolean;
}

export interface SetupStepView {
  text: string;
  code?: string;
  link?: { label: string; url: string };
}

export interface SetupSectionView {
  title: string;
  subtitle?: string;
  steps: SetupStepView[];
}

export interface SetupGuideView {
  intro?: string;
  sections: SetupSectionView[];
}

export interface IntegrationView {
  id: string;
  name: string;
  description: string;
  category: Category;
  docsUrl?: string;
  configured: boolean;
  fields: FieldView[];
  hasTest: boolean;
  actions?: IntegrationActionView[];
  setupGuide?: SetupGuideView;
}

export interface SmokeTestResultView {
  ok: boolean;
  summary?: string;
  error?: string;
  details?: unknown;
}

/**
 * Error thrown by `request()` on a non-2xx response. Still an `Error` with a
 * human-readable `.message` (existing `(e as Error).message` callers keep working),
 * but also carries the HTTP `status` and the parsed JSON `data` so callers can
 * branch on a structured error `code` (e.g. the publish "unfilled_image_markers"
 * guard) instead of string-matching the message.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly data: Record<string, unknown> | null;
  constructor(message: string, status: number, data: Record<string, unknown> | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Only set Content-Type when we actually have a body. Fastify v5 rejects
  // POST/PATCH with `Content-Type: application/json` and an empty body
  // (FST_ERR_CTP_EMPTY_JSON_BODY).
  const hasBody = init?.body !== undefined && init.body !== null;
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string> | undefined) };
  if (hasBody && !headers["Content-Type"]) headers["Content-Type"] = "application/json";
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    let errMsg = `${res.status} ${res.statusText}`;
    let data: Record<string, unknown> | null = null;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      data = body as Record<string, unknown>;
      if (body.error) errMsg = body.error;
      else if (body.message) errMsg = body.message;
    } catch {
      // body wasn't JSON — keep the status line
    }
    throw new ApiError(errMsg, res.status, data);
  }
  return res.json() as Promise<T>;
}

export type MaestroState =
  | "running" | "stopped" | "pending" | "stopping" | "shutting-down" | "terminated" | "unknown";

export interface MaestroStatus {
  state: MaestroState;
  /** True only when the box is running AND the Maestro proxy answers. */
  ready?: boolean;
  url: string;
  studioUrl?: string;
  instanceId?: string;
  region?: string;
  started?: boolean;
  stopped?: boolean;
  note?: string;
}


// ── Pipeline focus (migration 086) ──────────────────────────────────────────
export interface FocusView {
  id: string;
  label: string;
  methods: string[];
  clusterIds: string[];
  families: string[];
  share: number;
  active: boolean;
  endsAt: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}
export interface FocusOptions {
  methods: { slug: string; nameHe: string; openKeywords: number; therapists: number | null }[];
  families: { id: string; labelHe: string }[];
  presets: { id: string; labelHe: string; methods: string[] }[];
}
export interface FocusInputView {
  id?: string;
  label: string;
  methods: string[];
  clusterIds: string[];
  families: string[];
  share: number;
  active: boolean;
  endsAt: string | null;
}
export interface FocusPreview {
  ok: boolean;
  blocked?: string;
  maxArticles?: number;
  nextRun?: { keyword: string; focus: string | null }[];
  lanes?: { id: string; label: string; share: number; readyNow: number; waitingForEnrichment: number; inNextRun: number; inNextEnrichment: number }[];
}

export const api = {
  listIntegrations(): Promise<{ integrations: IntegrationView[] }> {
    return request("/api/integrations");
  },
  getIntegration(id: string): Promise<IntegrationView> {
    return request(`/api/integrations/${encodeURIComponent(id)}`);
  },
  patchIntegration(id: string, updates: Record<string, string>): Promise<IntegrationView> {
    return request(`/api/integrations/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
  },
  testIntegration(id: string): Promise<SmokeTestResultView> {
    return request(`/api/integrations/${encodeURIComponent(id)}/test`, { method: "POST" });
  },
  runIntegrationAction(id: string, actionId: string): Promise<SmokeTestResultView> {
    return request(`/api/integrations/${encodeURIComponent(id)}/actions/${encodeURIComponent(actionId)}`, { method: "POST" });
  },

  // ── Global search (⌘K) + notifications ────────────────────────────────────
  search(q: string): Promise<SearchResponse> {
    return request(`/api/search?q=${encodeURIComponent(q)}`);
  },
  notifications(): Promise<NotificationsResponse> {
    return request("/api/notifications");
  },

  // ── Dashboard (read) ──────────────────────────────────────────────────────
  getOverview(): Promise<OverviewResponse> {
    return request("/api/dashboard/overview");
  },
  getTasks(): Promise<TasksResponse> {
    return request("/api/dashboard/tasks");
  },

  // ── Articles (read) ────────────────────────────────────────────────────────
  listArticles(filter?: ArticleFilter): Promise<ArticlesResponse> {
    const qs = new URLSearchParams();
    if (filter?.language) qs.set("language", filter.language);
    if (filter?.cluster) qs.set("cluster", filter.cluster);
    if (filter?.q) qs.set("q", filter.q);
    if (filter?.sort) qs.set("sort", filter.sort);
    if (filter?.hasMeta) qs.set("hasMeta", "true");
    return request(`/api/articles${qs.size ? `?${qs.toString()}` : ""}`);
  },

  // ── Audience (read) ────────────────────────────────────────────────────────
  getAudienceOverview(): Promise<AudienceOverviewResponse> {
    return request("/api/audience/overview");
  },
  listAudienceContacts(opts: { listId?: number; limit?: number; offset?: number } = {}): Promise<AudienceContactsResponse> {
    const qs = new URLSearchParams();
    if (opts.listId !== undefined) qs.set("listId", String(opts.listId));
    if (opts.limit !== undefined) qs.set("limit", String(opts.limit));
    if (opts.offset !== undefined) qs.set("offset", String(opts.offset));
    return request(`/api/audience/contacts${qs.size ? `?${qs.toString()}` : ""}`);
  },

  // ── Discovery (sources + items + URL analyzer) ────────────────────────────
  listDiscoverySources(): Promise<{ count: number; sources: RssFeedView[] }> {
    return request("/api/discovery/sources");
  },
  // ── Source intake (competitor topics → proposals) ─────────────────────────
  listSourceProposals(f: { status?: SourceProposalStatus; outcome?: SourceProposalOutcome; includeOut?: boolean } = {}): Promise<{ rows: SourceProposalView[]; counts: Record<string, number> }> {
    const q = new URLSearchParams();
    if (f.status) q.set("status", f.status);
    if (f.outcome) q.set("outcome", f.outcome);
    if (f.includeOut) q.set("includeOut", "1");
    return request(`/api/source-intake/proposals?${q.toString()}`);
  },
  sourceIntakeUnenriched(): Promise<{ count: number; keywords: { id: string; keyword: string }[] }> {
    return request("/api/source-intake/unenriched");
  },
  enrichKeywords(keywordIds: string[]): Promise<{ ok: boolean; started?: boolean; message?: string; error?: string }> {
    return request("/api/automations/autopilot/enrich-queue", {
      method: "POST",
      body: JSON.stringify({ keywordIds, limit: Math.min(keywordIds.length, 800) }),
    });
  },
  sourceIntakeStatus(): Promise<SourceIntakeStatusView> {
    return request("/api/source-intake/status");
  },
  runSourceIntake(dryRun = false): Promise<{ ok: boolean; started?: boolean; error?: string }> {
    return request("/api/source-intake/run", { method: "POST", body: JSON.stringify({ dryRun }) });
  },
  decideSourceProposals(input: { ids: number[]; decision: "approve" | "reject"; asNew?: boolean; note?: string }): Promise<{
    ok: boolean; rejected?: number; placedByRules?: number; queuedForPlacement?: number;
    results?: { ok: boolean; proposalId: number; keywordId?: string; message: string }[];
  }> {
    return request("/api/source-intake/decide", { method: "POST", body: JSON.stringify(input) });
  },
  addDiscoverySource(input: RssFeedInputView): Promise<RssFeedView> {
    return request("/api/discovery/sources", { method: "POST", body: JSON.stringify(input) });
  },
  patchDiscoverySource(id: string, patch: Partial<RssFeedInputView>): Promise<RssFeedView> {
    return request(`/api/discovery/sources/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  deleteDiscoverySource(id: string): Promise<{ ok: boolean }> {
    return request(`/api/discovery/sources/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  pollDiscoverySource(id: string, opts?: { analyze?: boolean; maxItems?: number }): Promise<PollFeedResultView> {
    return request(`/api/discovery/sources/${encodeURIComponent(id)}/poll`, {
      method: "POST",
      body: JSON.stringify(opts ?? {}),
    });
  },
  pollAllDiscoverySources(opts?: { analyze?: boolean }): Promise<{
    count: number;
    results: PollFeedResultView[];
    totals: { itemsSeen: number; itemsNew: number; itemsAnalyzed: number };
  }> {
    return request("/api/discovery/poll-all", { method: "POST", body: JSON.stringify(opts ?? {}) });
  },
  getDiscoveryScheduler(): Promise<SchedulerStatusResponse> {
    return request("/api/discovery/scheduler");
  },
  runDiscoveryScheduler(): Promise<{ entry: SweepEntryView }> {
    return request("/api/discovery/scheduler/run", { method: "POST" });
  },
  analyzeUrl(url: string, skipLlm = false): Promise<{ discovery: DiscoveryView; warnings: string[] }> {
    return request("/api/discovery/analyze-url", { method: "POST", body: JSON.stringify({ url, skipLlm }) });
  },
  analyzeYoutube(url: string, skipLlm = false): Promise<{ discovery: DiscoveryView; warnings: string[] }> {
    return request("/api/discovery/analyze-youtube", { method: "POST", body: JSON.stringify({ url, skipLlm }) });
  },
  watchYoutubeChannel(body: { input: string; name?: string | null; entityId?: string | null; language?: "he" | "en" | "other"; autoAnalyze?: boolean }): Promise<WatchChannelResult> {
    return request("/api/discovery/youtube-channels", { method: "POST", body: JSON.stringify(body) });
  },
  listDiscoveryItems(filter?: DiscoveryItemFilter): Promise<{
    count: number;
    items: DiscoveryView[];
    countsByStatus: Record<string, number>;
  }> {
    const qs = new URLSearchParams();
    if (filter?.status) qs.set("status", filter.status);
    if (filter?.kind) qs.set("kind", filter.kind);
    if (filter?.sourceId) qs.set("sourceId", filter.sourceId);
    if (filter?.q) qs.set("q", filter.q);
    if (filter?.limit) qs.set("limit", String(filter.limit));
    if (filter?.offset) qs.set("offset", String(filter.offset));
    return request(`/api/discovery/items${qs.size ? `?${qs.toString()}` : ""}`);
  },
  patchDiscoveryItem(id: number, patch: { status?: DiscoveryStatusView; notes?: string | null; promotedToKeywordId?: string | null }): Promise<DiscoveryView> {
    return request(`/api/discovery/items/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  deleteDiscoveryItem(id: number): Promise<{ ok: boolean }> {
    return request(`/api/discovery/items/${id}`, { method: "DELETE" });
  },
  promoteDiscovery(id: number, body: PromoteDiscoveryInput): Promise<{ keyword: { id: string; keyword: string; language: Language }; discovery: DiscoveryView }> {
    return request(`/api/discovery/items/${id}/promote`, { method: "POST", body: JSON.stringify(body) });
  },
  suggestDiscoveryKeyword(body: { title: string; excerpt?: string | null; language?: string }): Promise<{
    candidates: { keyword: string; searchVolume: number | null; suggested: boolean }[];
    suggested: string | null;
    hasVolumes: boolean;
    note?: string;
  }> {
    return request(`/api/discovery/suggest-keyword`, { method: "POST", body: JSON.stringify(body) });
  },

  // ── Keyword Research (durable keyword_metrics store) ──────────────────────
  listKeywordResearch(q?: string): Promise<{ count: number; rows: KeywordResearchRow[] }> {
    return request(`/api/keyword-research${q ? `?q=${encodeURIComponent(q)}` : ""}`);
  },
  lookupKeywordResearch(body: { seed: string; mode?: "expand" | "exact"; language?: string }): Promise<{ ok: boolean; added: number; cost: number }> {
    return request(`/api/keyword-research/lookup`, { method: "POST", body: JSON.stringify(body) });
  },
  getResearchClusters(): Promise<{ clusters: ClusterRollup[] }> {
    return request(`/api/keyword-research/clusters`);
  },
  // ── Cluster Planner ────────────────────────────────────────────────────────
  plannerSeedTaxonomy(archiveLegacy = false): Promise<{ ok: boolean; summary: string; created: number; skippedExisting: number }> {
    return request(`/api/planner/taxonomy/seed`, { method: "POST", body: JSON.stringify({ archiveLegacy }) });
  },
  plannerTree(): Promise<PlannerTreeResponse> {
    return request(`/api/planner/tree`);
  },
  plannerSuggest(useLlm: boolean): Promise<{ ok: boolean; summary: string; scanned: number; deterministic: number; llm: number; unresolved: number; llmCalls: number; costUsd: number; warnings: string[] }> {
    return request(`/api/planner/suggest`, { method: "POST", body: JSON.stringify({ useLlm }) });
  },
  plannerAssignments(status?: "pending" | "accepted" | "rejected"): Promise<{ counts: { pending: number; accepted: number; rejected: number }; items: PlannerAssignmentView[] }> {
    return request(`/api/planner/assignments${status ? `?status=${status}` : ""}`);
  },
  plannerBulk(ids: string[], action: "accept" | "reject" | "reopen", kind: "assignment" | "keyword" = "assignment"): Promise<{ ok: boolean; succeeded: number; clustersCreated: number; failed: number; errors: { id: string; error: string }[] }> {
    return request(`/api/planner/assignments/bulk`, { method: "POST", body: JSON.stringify({ ids, action, kind }) });
  },
  plannerReassign(id: string, clusterId: string, kind: "assignment" | "keyword" = "assignment"): Promise<{ ok: boolean }> {
    return request(`/api/planner/assignments/${encodeURIComponent(id)}/reassign`, { method: "POST", body: JSON.stringify({ clusterId, kind }) });
  },
  plannerSetPrimary(id: string, kind: "assignment" | "keyword" = "assignment"): Promise<{ ok: boolean }> {
    return request(`/api/planner/assignments/${encodeURIComponent(id)}/primary`, { method: "POST", body: JSON.stringify({ kind }) });
  },
  plannerActivity(limit = 50): Promise<{ entries: PlannerActivityView[] }> {
    return request(`/api/planner/activity?limit=${limit}`);
  },
  plannerUndo(id: string): Promise<{ ok: boolean; restored: number; failed: number; summary: string }> {
    return request(`/api/planner/activity/${encodeURIComponent(id)}/undo`, { method: "POST" });
  },
  plannerCreatePiece(clusterId: string, allowRival = false): Promise<{ ok: boolean; summary: string; mode: "created" | "expand-existing" | "already-in-pipeline" | "folded-into-parent" | "parent-not-ready" | "rival-blocked"; keywordId: string | null; primary: string; secondaries: string[]; pageUrl: string | null; parentClusterId?: string | null; rival?: { url: string | null; kind: string | null; title: string | null; reason: string } }> {
    return request(`/api/planner/clusters/${encodeURIComponent(clusterId)}/create-piece`, { method: "POST", body: JSON.stringify({ allowRival }) });
  },
  plannerExpand(clusterId: string, useLlm = true): Promise<{ ok: boolean; summary: string; seed: string; fetched: number; dfsCost: number; deterministic: number; llm: number; unresolved: number; warnings: string[] }> {
    return request(`/api/planner/clusters/${encodeURIComponent(clusterId)}/expand`, { method: "POST", body: JSON.stringify({ useLlm }) });
  },
  plannerAudits(): Promise<PlannerAuditReport> {
    return request(`/api/planner/audits`);
  },
  getResearchCoverage(): Promise<CoverageReport> {
    return request(`/api/keyword-research/coverage`);
  },
  getResearchLinkGraph(): Promise<LinkGraph> {
    return request(`/api/keyword-research/link-graph`);
  },
  getLinkSuggestions(ids: string[]): Promise<{ suggestions: LinkSuggestion[] }> {
    return request(`/api/keyword-research/link-suggestions?ids=${encodeURIComponent(ids.join(","))}`);
  },
  /**
   * Starts the backlink pass in the BACKGROUND and returns as soon as it is
   * queued — the run takes minutes (a model call per generated sentence, per
   * host page, per target) and the gateway hangs up at 60 seconds, which used to
   * surface as a bare 504 while the server kept writing links. Poll
   * `getFixLinksStatus` for progress and results.
   */
  fixLinks(items: { url: string; title: string; keyword: string; clusterId?: string | null }[], max = 5): Promise<{
    ok: boolean;
    started: boolean;
    running: boolean;
    jobId: string;
    total: number;
  }> {
    return request(`/api/keyword-research/fix-links`, { method: "POST", body: JSON.stringify({ items, max }) });
  },
  getFixLinksStatus(): Promise<FixLinksStatus> {
    return request(`/api/keyword-research/fix-links-status`);
  },
  // ── Backlinks page: every inbound link our passes wrote, and its sentence ──
  removeBacklink(id: string): Promise<{ ok: boolean; eventId: number }> {
    return request(`/api/backlinks/placements/remove`, { method: "POST", body: JSON.stringify({ id }) });
  },
  restoreBacklink(eventId: number): Promise<{ ok: boolean }> {
    return request(`/api/backlinks/removed/restore`, { method: "POST", body: JSON.stringify({ eventId }) });
  },
  startBacklinkRelevance(force = false): Promise<{ ok: boolean; started: boolean }> {
    return request(`/api/backlinks/relevance/run`, { method: "POST", body: JSON.stringify({ force }) });
  },
  getBacklinkRelevanceStatus(): Promise<{ ok: boolean; job: BacklinkRelevanceJob | null }> {
    return request(`/api/backlinks/relevance/status`);
  },
  getBacklinkPlacements(refresh = false): Promise<{ ok: boolean; scannedAt: string; placements: BacklinkPlacement[]; removed?: RemovedBacklink[] }> {
    return request(`/api/backlinks/placements${refresh ? "?refresh=1" : ""}`);
  },
  suggestBacklinkSentence(id: string): Promise<{ ok: boolean; sentence: string; problems: string[] }> {
    return request(`/api/backlinks/placements/suggest`, { method: "POST", body: JSON.stringify({ id }) });
  },
  locateBacklinkText(id: string, sentence: string, anchor?: string): Promise<{ ok: boolean; sentence: string; linkText: string | null; start?: number; end?: number; error?: string; replaceable?: string }> {
    return request(`/api/backlinks/placements/locate`, { method: "POST", body: JSON.stringify({ id, sentence, ...(anchor ? { anchor } : {}) }) });
  },
  undoBacklinkEdit(id: string): Promise<{ ok: boolean; restored: string; learnedPhrase: string | null; placement: BacklinkPlacement | null }> {
    return request(`/api/backlinks/placements/undo`, { method: "POST", body: JSON.stringify({ id }) });
  },
  approveBacklinks(ids: string[], approved: boolean): Promise<{ ok: boolean; updated: number }> {
    return request(`/api/backlinks/placements/approve`, { method: "POST", body: JSON.stringify({ ids, approved }) });
  },
  getBacklinkLearning(): Promise<{ ok: boolean; lessons: BacklinkLesson[]; undistilledEdits: number; anchorForms: { targetUrl: string; phrase: string }[] }> {
    return request(`/api/backlinks/learning`);
  },
  distillBacklinkLessons(): Promise<{ ok: boolean; read: number; proposed: BacklinkLesson[] }> {
    return request(`/api/backlinks/learning/distill`, { method: "POST" });
  },
  decideBacklinkLesson(id: number, patch: { status?: BacklinkLesson["status"]; rule?: string }): Promise<{ ok: boolean; lessons: BacklinkLesson[] }> {
    return request(`/api/backlinks/learning/lessons/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  saveBacklinkSentence(id: string, sentence: string, override = false, anchor?: string): Promise<{ ok: boolean; previous: string; learnedPhrase?: string | null; placement: BacklinkPlacement | null }> {
    return request(`/api/backlinks/placements/save`, { method: "POST", body: JSON.stringify({ id, sentence, override, ...(anchor ? { anchor } : {}) }) });
  },
  bulkAssignCluster(keywordIds: string[], clusterId: string | null): Promise<{ ok: boolean; updated: number }> {
    return request(`/api/keyword-research/bulk`, { method: "POST", body: JSON.stringify({ action: "assign-cluster", keywordIds, clusterId }) });
  },
  importKeywordResearch(body: {
    rows: { keyword: string; searchVolume?: number | null; keywordDifficulty?: number | null; searchIntent?: string | null }[];
    enrich?: boolean;
    language?: string;
  }): Promise<{
    ok: boolean;
    imported: number;
    newCount: number;
    alreadyStored: number;
    inPipeline: { keyword: string; status: string }[];
    published: string[];
    enriched: number;
    cost: number;
  }> {
    return request(`/api/keyword-research/import`, { method: "POST", body: JSON.stringify(body) });
  },
  fetchKeywordVolumes(body: { keywords?: string[]; language?: string }): Promise<{ ok: boolean; requested: number; fetched: number; cost: number }> {
    return request(`/api/keyword-research/fetch-volumes`, { method: "POST", body: JSON.stringify(body) });
  },
  editKeywordMetric(body: {
    keyword: string;
    language?: string;
    searchVolume?: number | null;
    keywordDifficulty?: number | null;
    searchIntent?: string | null;
    cpc?: number | null;
  }): Promise<{ ok: boolean }> {
    return request(`/api/keyword-research/metric`, { method: "PATCH", body: JSON.stringify(body) });
  },

  // ── Maestro Studio (external GPU box) ─────────────────────────────────────
  maestroStatus(): Promise<MaestroStatus> {
    return request("/api/maestro/status");
  },
  maestroStart(): Promise<MaestroStatus> {
    return request("/api/maestro/start", { method: "POST" });
  },
  maestroStop(): Promise<MaestroStatus> {
    return request("/api/maestro/stop", { method: "POST" });
  },

  // ── Distribution log ──────────────────────────────────────────────────────
  getDistributionOverview(): Promise<DistributionOverviewResponse> {
    return request("/api/distribution/overview");
  },
  listDistributions(filter?: DistributionFilter): Promise<{ count: number; sends: DistributionRowView[] }> {
    const qs = new URLSearchParams();
    if (filter?.channel) qs.set("channel", filter.channel);
    if (filter?.status) qs.set("status", filter.status);
    if (filter?.keywordId) qs.set("keywordId", filter.keywordId);
    if (filter?.limit) qs.set("limit", String(filter.limit));
    return request(`/api/distribution/sends${qs.size ? `?${qs.toString()}` : ""}`);
  },
  getDistribution(id: number): Promise<{ send: DistributionRowView; keyword: { id: string; keyword: string; language: Language; cluster: string | null; h1: string | null } | null }> {
    return request(`/api/distribution/sends/${id}`);
  },

  // ── Pipeline (read) ────────────────────────────────────────────────────────
  listClusters(): Promise<{ clusters: ClusterView[] }> {
    return request("/api/pipeline/clusters");
  },
  createCluster(input: CreateClusterInput): Promise<{ ok: boolean; cluster: ClusterView; error?: string }> {
    return request("/api/clusters", { method: "POST", body: JSON.stringify(input) });
  },
  /** Classifier assignment. Max 8 ids per call — use `aiAssignClustersAll` for more. */
  aiAssignClusters(keywordIds: string[], apply = true): Promise<{ ok: boolean; applied: boolean; results: AiAssignOutcome[] }> {
    return request("/api/clusters/ai-assign", { method: "POST", body: JSON.stringify({ keywordIds, apply }) });
  },
  /**
   * Any number of keywords, in batches of 8. Batched from the client rather than
   * one long request: a single call over 60s is a CloudFront 504 while the
   * server keeps writing, and the natural retry would classify everything twice.
   */
  async aiAssignClustersAll(keywordIds: string[], onProgress?: (done: number, total: number) => void): Promise<AiAssignOutcome[]> {
    const out: AiAssignOutcome[] = [];
    for (let i = 0; i < keywordIds.length; i += 8) {
      const r = await api.aiAssignClusters(keywordIds.slice(i, i + 8));
      out.push(...r.results);
      onProgress?.(Math.min(i + 8, keywordIds.length), keywordIds.length);
    }
    return out;
  },
  patchCluster(id: string, input: EditClusterInput): Promise<{ ok: boolean; cluster: ClusterView; error?: string }> {
    return request(`/api/clusters/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(input) });
  },
  reparentCluster(id: string, input: { parentClusterId?: string | null; family?: ClusterFamily | null; subFamily?: string | null }): Promise<{ ok: boolean; cluster: ClusterView; error?: string }> {
    return request(`/api/clusters/${encodeURIComponent(id)}/reparent`, { method: "POST", body: JSON.stringify(input) });
  },
  archiveCluster(id: string): Promise<{ ok: boolean; cluster: ClusterView; summary?: string; reparentedChildren?: number; error?: string }> {
    return request(`/api/clusters/${encodeURIComponent(id)}/archive`, { method: "POST" });
  },
  reconcileSections(): Promise<{ ok: boolean; summary: string; moved: number; changes: { cluster: string; from: string; to: string }[] }> {
    return request("/api/planner/reconcile-sections", { method: "POST" });
  },
  listKeywords(filter?: KeywordFilter): Promise<{ count: number; shown?: number; keywords: KeywordSummary[] }> {
    const qs = new URLSearchParams();
    if (filter?.clusterId) qs.set("clusterId", filter.clusterId);
    if (filter?.language) qs.set("language", filter.language);
    if (filter?.status && filter.status.length === 1 && filter.status[0]) qs.set("status", filter.status[0]);
    if (filter?.q) qs.set("q", filter.q);
    return request(`/api/pipeline/keywords${qs.size ? `?${qs.toString()}` : ""}`);
  },
  getKeyword(id: string): Promise<KeywordDetail> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}`);
  },

  // ── Autopilot pre-flight ────────────────────────────────────────────────
  /**
   * Link the model-chosen siblings as secondaries of `primaryKeywordId`.
   * Partial results are normal — the server refuses to overwrite a sibling that
   * already belongs to another primary, is a primary itself, or has an article.
   */
  foldSecondaries(
    primaryKeywordId: string,
    siblingIds: string[],
    undo = false,
  ): Promise<{
    ok: boolean;
    linked: { id: string; keyword: string }[];
    skipped: { id: string; keyword: string; reason: string }[];
  }> {
    return request("/api/automations/autopilot/preflight/fold", {
      method: "POST",
      body: JSON.stringify({ primaryKeywordId, siblingIds, undo }),
    });
  },

  /** Dismiss a pre-flight finding. Sticks across future runs, keyed (keyword, code). */
  ackPreflightFinding(keywordId: string, code: string, undo = false): Promise<{ ok: boolean; acknowledged: boolean }> {
    return request("/api/automations/autopilot/preflight/ack", {
      method: "POST",
      body: JSON.stringify({ keywordId, code, undo }),
    });
  },
  /** Ask the model which known fix closes each open finding. READ-ONLY — returns a plan. */
  proposeResolutions(keywordIds?: string[]): Promise<{ ok: boolean; plan: StoredPlan | null }> {
    return request("/api/automations/autopilot/preflight/resolve", {
      method: "POST",
      body: JSON.stringify(keywordIds?.length ? { keywordIds } : {}),
    });
  },
  /** The plan awaiting a decision, if any. Survives navigation and reloads. */
  getPendingPlan(): Promise<{
    ok: boolean;
    plan: StoredPlan | null;
    /** Non-null while the server is computing a proposal, whoever started it. */
    inFlight?: { startedAt: string; keyword: string | null; elapsedMs: number } | null;
  }> {
    return request("/api/automations/autopilot/preflight/plan");
  },
  /** Tick / untick one step. Persisted, so a half-reviewed plan survives a reload. */
  approvePlanFix(fixId: number, approved: boolean): Promise<{ ok: boolean; plan: StoredPlan | null }> {
    return request("/api/automations/autopilot/preflight/plan/approve", {
      method: "POST",
      body: JSON.stringify({ fixId, approved }),
    });
  },
  /** Commit the approved steps. */
  applyPlan(planId: number, fixIds?: number[]): Promise<{ ok: boolean; results: { keyword: string; action: string; ok: boolean; detail: string }[] }> {
    return request("/api/automations/autopilot/preflight/plan/apply", {
      method: "POST",
      body: JSON.stringify(fixIds?.length ? { planId, fixIds } : { planId }),
    });
  },
  /** Reject it wholesale — releases the production hold on its keywords. */
  discardPlan(planId: number): Promise<{ ok: boolean }> {
    return request("/api/automations/autopilot/preflight/plan/discard", {
      method: "POST",
      body: JSON.stringify({ planId }),
    });
  },

  /** Apply the steps you approved. Re-validated server-side against live rows. */
  applyResolutions(fixes: PlannedFix[]): Promise<{
    ok: boolean;
    results: { keyword: string; action: string; ok: boolean; detail: string }[];
  }> {
    return request("/api/automations/autopilot/preflight/resolve/apply", {
      method: "POST",
      body: JSON.stringify({ fixes }),
    });
  },

  /**
   * "Ignore for now" — hold a keyword out of production without deciding about
   * it. Distinct from dismissing a finding, which means "judged, go ahead".
   */
  deferKeyword(keywordId: string, opts: { until?: string | null; note?: string | null; resume?: boolean } = {}): Promise<{
    ok: boolean;
    deferred: boolean;
    promoted: string[];
  }> {
    return request("/api/automations/autopilot/preflight/defer", {
      method: "POST",
      body: JSON.stringify({ keywordId, ...opts }),
    });
  },

  /** Re-fetch the SERP — resolves the enrichment-depth findings. */
  reEnrichKeyword(keywordId: string): Promise<{ ok: boolean; paaQuestions: number; competitors: number }> {
    return request("/api/automations/autopilot/preflight/re-enrich", {
      method: "POST",
      body: JSON.stringify({ keywordId }),
    });
  },

  /** Background: enrich the autopilot's top N not-enriched candidates (status untouched). */
  startEnrichQueue(limit: number): Promise<{ ok: boolean; started: boolean; message?: string; job?: EnrichQueueJobView | null }> {
    return request("/api/automations/autopilot/enrich-queue", { method: "POST", body: JSON.stringify({ limit }) });
  },
  getEnrichQueue(): Promise<{ ok: boolean; running: boolean; job: EnrichQueueJobView | null }> {
    return request("/api/automations/autopilot/enrich-queue");
  },
  getSerpApiQuota(): Promise<{ ok: boolean; quota: SerpApiQuotaView; logged?: SerpApiLoggedUsageView | null }> {
    return request("/api/automations/serpapi-quota");
  },

  // ── Manual add + CSV import ─────────────────────────────────────────────
  addKeyword(body: AddKeywordInput): Promise<KeywordSummary> {
    return request("/api/pipeline/keywords", { method: "POST", body: JSON.stringify(body) });
  },
  patchKeyword(id: string, body: PatchKeywordInput): Promise<{ ok: boolean; keyword: KeywordSummary }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  deleteKeyword(id: string): Promise<{ ok: boolean; deletedId: string }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  bulkKeywordAction(body: BulkKeywordActionInput): Promise<BulkKeywordActionResponse> {
    return request("/api/pipeline/keywords/bulk", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  importPreview(csvContent: string): Promise<ImportPreviewResponse> {
    return request("/api/keywords/import/preview", {
      method: "POST",
      body: JSON.stringify({ csvContent }),
    });
  },
  importCannibalization(csvContent: string, mappings: ImportMapping[]): Promise<CannibalizationResponse> {
    return request("/api/keywords/import/cannibalization", {
      method: "POST",
      body: JSON.stringify({ csvContent, mappings }),
    });
  },
  importTriage(csvContent: string, mappings: ImportMapping[]): Promise<ImportTriageResponse> {
    return request("/api/keywords/import/triage", {
      method: "POST",
      body: JSON.stringify({ csvContent, mappings }),
    });
  },
  importCommit(body: ImportCommitInput): Promise<ImportCommitResponse> {
    return request("/api/keywords/import/commit", { method: "POST", body: JSON.stringify(body) });
  },

  // ── Cluster expansion ─────────────────────────────────────────────────────
  expandCluster(clusterId: string, body?: ExpandClusterInput): Promise<ExpandClusterResponse> {
    return request(`/api/clusters/${encodeURIComponent(clusterId)}/expand`, {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    });
  },
  listSuggestions(clusterId: string, status?: SuggestionStatus): Promise<{ clusterId: string; count: number; suggestions: SuggestionView[] }> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    return request(`/api/clusters/${encodeURIComponent(clusterId)}/suggestions${qs}`);
  },
  acceptSuggestion(clusterId: string, suggestionId: string, body?: AcceptSuggestionInput): Promise<{ ok: boolean; keywordId: string; alreadyExisted: boolean; suggestion: SuggestionView }> {
    return request(`/api/clusters/${encodeURIComponent(clusterId)}/suggestions/${encodeURIComponent(suggestionId)}/accept`, {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    });
  },
  rejectSuggestion(clusterId: string, suggestionId: string, notes?: string): Promise<{ ok: boolean; suggestion: SuggestionView }> {
    return request(`/api/clusters/${encodeURIComponent(clusterId)}/suggestions/${encodeURIComponent(suggestionId)}/reject`, {
      method: "POST",
      body: JSON.stringify({ notes }),
    });
  },
  reopenSuggestion(clusterId: string, suggestionId: string): Promise<{ ok: boolean; suggestion: SuggestionView }> {
    return request(`/api/clusters/${encodeURIComponent(clusterId)}/suggestions/${encodeURIComponent(suggestionId)}/reopen`, {
      method: "POST",
    });
  },
  listClusterProposals(): Promise<{ count: number; proposals: ClusterProposalView[] }> {
    return request("/api/cluster-proposals");
  },

  // ── Content database (live Sanity mirror) ──────────────────────────────
  getPublishedOverview(): Promise<{ counts: { article: number; treatment_category: number; treatment_subcategory: number; total: number }; lastSyncedAt: string | null }> {
    return request("/api/published-articles/overview");
  },
  listPublishedArticles(filters?: { kind?: PublishedKind; clusterId?: string; isLive?: boolean; q?: string }): Promise<{ count: number; articles: PublishedArticleView[] }> {
    const qs = new URLSearchParams();
    if (filters?.kind) qs.set("kind", filters.kind);
    if (filters?.clusterId) qs.set("clusterId", filters.clusterId);
    if (filters?.isLive !== undefined) qs.set("isLive", String(filters.isLive));
    if (filters?.q) qs.set("q", filters.q);
    return request(`/api/published-articles${qs.size ? `?${qs.toString()}` : ""}`);
  },
  syncGsc(body: { onlySanityId?: string } = {}): Promise<{ ok: boolean; summary?: string; attempted: number; succeeded: number; failed: number; totalImpressions: number; totalClicks: number; durationMs: number; warnings: string[]; error?: string }> {
    return request(`/api/published-articles/gsc/sync`, { method: "POST", body: JSON.stringify(body) });
  },
  patchPublishedArticle(sanityId: string, body: { urlOverride?: string | null; urlOverrideNote?: string | null; mainKeyword?: string | null; secondaryKeywords?: string[] }): Promise<{ ok: boolean; article: PublishedArticleView }> {
    return request(`/api/published-articles/${encodeURIComponent(sanityId)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  getKeywordSuggestions(sanityId: string): Promise<{ main: string[]; secondary: string[] }> {
    return request(`/api/published-articles/${encodeURIComponent(sanityId)}/keyword-suggestions`);
  },
  moveToPipeline(sanityId: string): Promise<{ ok: boolean; keywordId?: string; mode?: "created" | "already-in-pipeline"; summary?: string; error?: string }> {
    return request(`/api/published-articles/${encodeURIComponent(sanityId)}/move-to-pipeline`, { method: "POST" });
  },
  /**
   * Starts the sync as a server-side job and polls it to completion (a long
   * sync used to 504 at CloudFront's 60s while the server finished it fine).
   * Also attaches to a sync that is already running instead of failing.
   */
  async syncPublishedArticles(
    body?: { skipLlm?: boolean; forceReclassify?: boolean },
    onProgress?: (job: PublishedSyncJobView) => void,
  ): Promise<PublishedSyncResponse> {
    await request<{ ok: boolean; started: boolean; alreadyRunning?: boolean }>("/api/published-articles/sync", {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    });
    for (;;) {
      await new Promise((r) => setTimeout(r, 2000));
      const job = await request<PublishedSyncJobView>("/api/published-articles/sync/status");
      onProgress?.(job);
      if (job.status === "done" && job.result) return job.result;
      if (job.status === "error") throw new Error(job.error ?? "sync failed");
      // "idle": the running sync was the scheduler's, which reports no job
      // here. Its result lands in the table; nothing more to wait for.
      if (job.status === "idle") {
        return { ok: true, summary: "A scheduled sync was already running; the table below shows its result.", warnings: [] } as unknown as PublishedSyncResponse;
      }
    }
  },
  reconcileStatus(body?: { dryRun?: boolean }): Promise<ReconcileStatusResponse> {
    return request("/api/published-articles/reconcile-status", { method: "POST", body: JSON.stringify(body ?? {}) });
  },
  classifyUnclustered(body?: { dryRun?: boolean; threshold?: number; limit?: number }): Promise<ClassifyUnclusteredResponse> {
    return request("/api/published-articles/classify-unclustered", {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    });
  },
  approveClusterProposal(proposalId: string, body?: ApproveClusterProposalInput): Promise<{ ok: boolean; cluster: ClusterView; acceptedKeywordIds: string[]; skipped: { suggestionId: string; error: string }[] }> {
    return request(`/api/cluster-proposals/${encodeURIComponent(proposalId)}/approve`, {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    });
  },
  getSeoScore(id: string): Promise<{ ok: boolean; score?: SeoScoreView; error?: string }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/seo-score`);
  },
  listAuthors(): Promise<{ authors: AuthorRecordView[] }> {
    return request("/api/authors");
  },
  createGuestPost(body: GuestPostInput): Promise<{ ok: boolean; keywordId?: string; summary?: string; warnings?: string[]; error?: string }> {
    return request("/api/pipeline/guest-post", { method: "POST", body: JSON.stringify(body) });
  },
  weaveLinks(body: { keyword: string; bodyMarkdown: string; title?: string; language?: "he" | "en"; clusterId?: string }): Promise<WeaveLinksResponse> {
    return request("/api/pipeline/weave-links", { method: "POST", body: JSON.stringify(body) });
  },
  uploadImage(dataUrl: string): Promise<{ ok: boolean; assetRef?: string; url?: string; error?: string }> {
    return request("/api/pipeline/author-image", { method: "POST", body: JSON.stringify({ dataUrl }) });
  },

  // ── Pipeline (actions, sync) ──────────────────────────────────────────────
  enrichKeyword(id: string): Promise<ActionResult> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/enrich`, { method: "POST" });
  },
  enrichCompetitors(id: string): Promise<ActionResult> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/enrich-competitors`, { method: "POST" });
  },
  /** Starts a BACKGROUND run and returns at once — poll `briefStatus`. A brief takes ~2 minutes. */
  generateBrief(id: string, body: { allowDuplicate?: boolean } = {}): Promise<ActionResult & { started?: boolean; running?: boolean }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/brief`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  briefStatus(id: string): Promise<{
    keywordId: string;
    status: "idle" | "running" | "done" | "error";
    startedAt?: string;
    finishedAt?: string | null;
    error?: string | null;
    summary?: string | null;
    warnings?: string[];
  }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/brief-status`);
  },
  patchBriefFields(id: string, fields: { outlineH1?: string; metaTitle?: string; metaDescription?: string; excerpt?: string }): Promise<{ ok: boolean; brief: unknown }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/brief`, { method: "PATCH", body: JSON.stringify(fields) });
  },
  /** Addressable markdown blocks of the produced article — the unit a targeted edit operates on. */
  getArticleBlocks(id: string): Promise<{ ok: boolean; blocks: { index: number; markdown: string; kind: string }[] }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/article/blocks`);
  },
  /** Rewrite one passage from an instruction. Returns a proposal — nothing is saved. */
  editArticleBlock(id: string, blockStart: number, blockEnd: number, instruction: string, selectedText?: string): Promise<{
    ok: boolean;
    before: string;
    after: string;
    fullMarkdown: string;
    note: string | null;
    estimatedCostUsd: number | null;
    error?: string;
  }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/article/edit-block`, {
      method: "POST",
      body: JSON.stringify({ blockStart, blockEnd, instruction, ...(selectedText ? { selectedText } : {}) }),
    });
  },
  /** Point this keyword at a live article so Publish updates that page in place. Writes three fields, publishes nothing. */
  mergeIntoLive(id: string, sanityId: string): Promise<{ ok: boolean; summary?: string; error?: string; target?: { sanityId: string; url: string; title: string; publishedAt: string | null } }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/merge-into-live`, { method: "POST", body: JSON.stringify({ sanityId }) });
  },
  listSplitProposals(clusterId: string): Promise<{ ok: boolean; proposals: SplitProposalView[] }> {
    return request(`/api/cluster-splits?clusterId=${encodeURIComponent(clusterId)}`);
  },
  proposeSplit(clusterId: string): Promise<{ ok: boolean; started?: boolean; running?: boolean; error?: string }> {
    return request(`/api/cluster-splits/propose`, { method: "POST", body: JSON.stringify({ clusterId }) });
  },
  splitProposeStatus(clusterId: string): Promise<{ ok: boolean; job: { status: "running" | "done" | "failed"; proposalId: string | null; error: string | null } | null }> {
    return request(`/api/cluster-splits/propose-status?clusterId=${encodeURIComponent(clusterId)}`);
  },
  applySplit(proposalId: string, dryRun: boolean): Promise<{ ok: boolean; error?: string; result?: SplitApplyView }> {
    return request(`/api/cluster-splits/${encodeURIComponent(proposalId)}/apply-stored`, { method: "POST", body: JSON.stringify({ dryRun }) });
  },
  revertSplit(proposalId: string): Promise<{ ok: boolean; error?: string; result?: { movedBack: number; removed: number } }> {
    return request(`/api/cluster-splits/${encodeURIComponent(proposalId)}/revert`, { method: "POST", body: "{}" });
  },
  discardSplit(proposalId: string): Promise<{ ok: boolean; error?: string }> {
    return request(`/api/cluster-splits/${encodeURIComponent(proposalId)}/discard`, { method: "POST", body: "{}" });
  },
  /** Required-links + Hebrew-proofread passes over a produced article. Preview unless `apply`. */
  repairArticle(id: string, body: { apply?: boolean } = {}): Promise<{ ok: boolean; applied?: boolean; summary?: string; bodyMarkdown?: string; metaChanged?: boolean; warnings?: string[]; costUsd?: number; error?: string }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/repair-article`, { method: "POST", body: JSON.stringify(body) });
  },
  patchArticleBody(id: string, articleMarkdown: string): Promise<{ ok: boolean; summary?: string; error?: string }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/article`, { method: "PATCH", body: JSON.stringify({ articleMarkdown }) });
  },
  regenerateBriefMeta(id: string): Promise<{ ok: boolean; summary: string; meta: { outlineH1: string; metaTitle: string; metaDescription: string } }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/brief/meta`, { method: "POST" });
  },
  refreshArticle(id: string): Promise<ActionResult> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/refresh`, { method: "POST" });
  },
  getKeywordCoverage(id: string): Promise<{ ok: boolean; coverage: CoverageResult }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/coverage`);
  },
  /**
   * `postiz` is RETIRED — the route answers 410 and points at the Social tab.
   * It stays in the union only so the Distribution log, which renders historical
   * rows, still type-checks.
   */
  distribute(id: string, channel: "telegram" | "brevo" | "youtube" | "facebook" | "instagram" | "whatsapp" | "x" | "tiktok", body: Record<string, unknown>): Promise<ActionResult> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/distribute/${channel}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  generateStoryboard(id: string, body: { targetSec?: number; aspectRatio?: "9:16" | "1:1" | "16:9"; model?: string } = {}): Promise<ActionResult> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/storyboard`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  saveStoryboard(id: string, storyboard: EditableStoryboard): Promise<ActionResult> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/storyboard/save`, {
      method: "POST",
      body: JSON.stringify({ storyboard }),
    });
  },
  generateVoiceover(id: string, body: { mode?: "article" | "scenes"; voiceId?: string; model?: string; script?: string } = {}): Promise<ActionResult> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/voiceover`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  generateViral(id: string, body: { skipSceneVideos?: boolean; skipVoiceovers?: boolean; musicPath?: string; music?: boolean; force?: boolean } = {}): Promise<ActionResult> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/viral`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  publishToSanity(id: string, body: { publish?: boolean; documentId?: string; dryRun?: boolean; mainImage?: boolean; allowMissingImages?: boolean; allowBrokenLinks?: boolean; refreshTaxonomy?: boolean; allowDuplicate?: boolean } = {}): Promise<ActionResult & { isDraft?: boolean; studioUrl?: string | null; imagesResolved?: number; imagesUnresolved?: number; resolvedPublishedAt?: string | null; wasFirstPublish?: boolean; writeMode?: "patch" | "createOrReplace" }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/publish-sanity`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  /** Ask the server to write a fresh image prompt for one slot. No image is generated. */
  suggestImagePrompt(id: string, slot: string): Promise<{ ok: boolean; prompt?: string; visual?: string; warnings?: string[]; estimatedCostUsd?: number; error?: string }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/images/suggest-prompt`, {
      method: "POST",
      body: JSON.stringify({ slot }),
    });
  },
  reviewAccuracy(id: string): Promise<{ ok: boolean; findings: AccuracyFinding[]; highCount: number; mediumCount: number; summary: string; modelUsed: string; estimatedCostUsd: number | null; error?: string }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/review-accuracy`, { method: "POST" });
  },
  // ── Pipeline focus ──────────────────────────────────────────────────────
  getFocus(): Promise<{ foci: FocusView[]; activeIds: string[]; freeShare: number; options: FocusOptions }> {
    return request("/api/focus");
  },
  getFocusPreview(): Promise<FocusPreview> {
    return request("/api/focus/preview");
  },
  saveFocus(body: FocusInputView): Promise<{ ok: boolean; focus: FocusView }> {
    return request("/api/focus", { method: "POST", body: JSON.stringify(body) });
  },
  deleteFocus(id: string): Promise<{ ok: boolean; deletedId: string }> {
    return request(`/api/focus/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  // ── Always-link targets ─────────────────────────────────────────────────
  listAlwaysLinks(): Promise<{ targets: AlwaysLinkTarget[] }> {
    return request("/api/always-links");
  },
  upsertAlwaysLink(body: { id?: string; url: string; mainAnchor: string; secondaryAnchors?: string[]; sortOrder?: number; active?: boolean }): Promise<{ ok: boolean; target: AlwaysLinkTarget }> {
    return request("/api/always-links", { method: "POST", body: JSON.stringify(body) });
  },
  deleteAlwaysLink(id: string): Promise<{ ok: boolean; deletedId: string }> {
    return request(`/api/always-links/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  // ── /explore provider anchors ───────────────────────────────────────────
  listExploreAnchors(): Promise<{ items: ExploreAnchorItem[]; total: number; unseeded: number }> {
    return request("/api/explore-anchors");
  },
  upsertExploreAnchor(body: { methodSlug: string; anchor: string }): Promise<{ ok: boolean; anchor: ExploreAnchorItem }> {
    return request("/api/explore-anchors", { method: "POST", body: JSON.stringify(body) });
  },
  deleteExploreAnchor(methodSlug: string): Promise<{ ok: boolean; resetSlug: string; anchor: { anchor: string; source: string } }> {
    return request(`/api/explore-anchors/${encodeURIComponent(methodSlug)}`, { method: "DELETE" });
  },
  syncExploreAnchors(): Promise<{ ok: boolean; summary: string; added: { slug: string; anchor: string }[]; missingName: string[] }> {
    return request("/api/explore-anchors/sync", { method: "POST" });
  },
  // ── Therapist supply per method ─────────────────────────────────────────
  listMethodSupply(): Promise<MethodSupplyResponse> {
    return request("/api/method-supply");
  },
  syncMethodSupply(): Promise<{ ok: boolean; summary: string; therapistsTotal: number; methods: number; cities: number; unknownMethods: string[] }> {
    return request("/api/method-supply/sync", { method: "POST" });
  },
  // ── Spotlight templates ─────────────────────────────────────────────────
  // ── Social packs ───────────────────────────────────────────────────────
  generateSocialPack(
    keywordId: string,
    body: { platforms?: SocialPlatform[]; articleUrl?: string; videoPolicy?: "generate" | "existing-only" } = {},
  ): Promise<GenerateSocialPackResponse> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(keywordId)}/social-pack`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  listSocialPacks(o: { keywordId?: string; status?: string; limit?: number } = {}): Promise<{ packs: SocialPackView[] }> {
    const q = new URLSearchParams();
    if (o.keywordId) q.set("keywordId", o.keywordId);
    if (o.status) q.set("status", o.status);
    if (o.limit) q.set("limit", String(o.limit));
    return request(`/api/social/packs${q.toString() ? `?${q.toString()}` : ""}`);
  },
  getSocialPack(id: string): Promise<{ pack: SocialPackView; variants: SocialVariantView[] }> {
    return request(`/api/social/packs/${encodeURIComponent(id)}`);
  },
  /**
   * A copy edit on a variant that already exists in Postiz is pushed through —
   * as a withdraw and a re-create, because Postiz has no content-edit endpoint.
   * `postiz` says what happened to the remote post, and is absent when there was
   * nothing remote to change.
   */
  updateSocialVariant(id: number, patch: SocialVariantPatch): Promise<{
    ok: boolean; variant: SocialVariantView;
    /**
     * The platform checks, re-run because the copy changed. Absent when it did
     * not — a reschedule cannot break a caption length.
     */
    checks?: { ok: boolean; issues: SocialCheckIssue[] };
    /** The copy names a holiday/season this post does not go out in. Warning only. */
    seasonal?: SeasonalIssueView[];
    postiz?: { ok: true; postId: string | null; withdrew: string | null } | { ok: false; error: string };
  }> {
    return request(`/api/social/variants/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  /**
   * Re-write one post with the model. SPENDS MONEY — measured at $0.037 for a
   * caption-only format and $0.055 for a carousel, which also re-writes the
   * deck, against $0.165 for a whole pack — and takes ~25 seconds.
   */
  regenerateSocialVariant(
    id: number,
    steer?: string,
    deck?: { goal?: DeckGoalKey; contentSlides?: number },
  ): Promise<{ ok: boolean; started: true; job: RegenerateJobView }> {
    return request(`/api/social/variants/${id}/regenerate`, {
      method: "POST",
      body: JSON.stringify({
        ...(steer ? { steer } : {}),
        ...(deck?.goal ? { goal: deck.goal } : {}),
        ...(deck?.contentSlides ? { contentSlides: deck.contentSlides } : {}),
      }),
    });
  },
  /** Deck posts that say too little or less than they promise, not yet published. */
  thinDecks(): Promise<ThinDecksView> {
    return request("/api/social/thin-decks");
  },
  /** Re-cut a story from its pack's carousel. Free: no model call. */
  recutStory(id: number): Promise<{ ok: boolean; before: number; after: number; postiz: { ok: boolean; error?: string } | null }> {
    return request(`/api/social/variants/${id}/recut`, { method: "POST", body: JSON.stringify({}) });
  },
  /** Re-cut every thin story not yet in Postiz. Local only. */
  recutLocalStories(): Promise<{ ok: boolean; recut: { id: number; before: number; after: number }[] }> {
    return request("/api/social/thin-decks/recut-local", { method: "POST", body: JSON.stringify({}) });
  },
  /** The current or most recent rewrite of one post; `variant` is present once it succeeded. */
  regenerateStatus(id: number): Promise<{ job: RegenerateJobView | null; variant?: SocialVariantView }> {
    return request(`/api/social/variants/${id}/regenerate-status`);
  },
  /**
   * Take a post off the calendar. `unschedule` returns it to the pool as an
   * undated draft; `remove` marks it skipped so no fill places it again. A copy
   * already in Postiz is withdrawn there first.
   */
  unscheduleSocialVariant(id: number, mode: "unschedule" | "remove"): Promise<{
    ok: boolean; variant: SocialVariantView; withdrew?: string;
  }> {
    return request(`/api/social/variants/${id}/unschedule`, { method: "POST", body: JSON.stringify({ mode }) });
  },
  /**
   * Undo a `remove`: the post goes back to being an undated draft the next fill
   * can place. Local only — removing it already withdrew the Postiz copy.
   */
  restoreSocialVariant(id: number): Promise<{ ok: boolean; variant: SocialVariantView }> {
    return request(`/api/social/variants/${id}/restore`, { method: "POST", body: JSON.stringify({}) });
  },
  /**
   * `draft` (default) creates a Postiz draft that publishes nothing until it is
   * approved there. `schedule` is approved on send and WILL publish at the slot;
   * the server refuses it for a post not approved here or whose slot has passed.
   */
  sendSocialVariant(id: number, mode: "draft" | "schedule" = "draft"): Promise<{ ok: boolean; postId?: string; uploaded: number; caption: string; error?: string; retryAfterSec?: number }> {
    return request(`/api/social/variants/${id}/send`, { method: "POST", body: JSON.stringify({ mode }) });
  },
  /** Postiz's hourly create limit as last seen. Null until the server has created a post. */
  postizQuota(): Promise<{ quota: { limit: number | null; remaining: number | null; blockedUntil: string | null; seenAt: string } | null }> {
    return request("/api/social/postiz-quota");
  },
  // ── Learning from edits ───────────────────────────────────────────────────
  // ── The Video page: the pipeline seen from outside one article ────────────
  videoQueue(): Promise<VideoQueueView> {
    return request("/api/video/queue");
  },
  videoRuns(): Promise<VideoRunsView> {
    return request("/api/video/runs");
  },
  videoLibrary(): Promise<VideoLibraryView> {
    return request("/api/video/library");
  },
  videoReels(): Promise<VideoReelsView> {
    return request("/api/video/reels");
  },
  /**
   * Start the whole chain for one article. It stops at both gates on its own;
   * `budgetCapUsd` refuses a step that would take it past the ceiling.
   */
  videoRunStart(body: { keywordId: string; budgetCapUsd?: number }): Promise<{ ok: boolean; run?: VideoRunRowView; error?: string }> {
    return request("/api/video/runs", { method: "POST", body: JSON.stringify(body) });
  },
  /** Start the next `count` runnable articles in the queue, under one ceiling. */
  videoRunBatch(body: { count: number; budgetCapUsd: number }): Promise<{
    ok: boolean; batchId: string; started: number; refused: string[]; perRunCapUsd: number;
  }> {
    return request("/api/video/runs/batch", { method: "POST", body: JSON.stringify(body) });
  },
  videoRunCancel(id: number): Promise<{ ok: boolean; error?: string }> {
    return request(`/api/video/runs/${id}/cancel`, { method: "POST" });
  },
  socialVoice(): Promise<{
    lessons: VoiceLessonView[]; pendingEdits: number; recentEdits: CopyEditView[]; distillRunning: boolean;
  }> {
    return request("/api/social/voice");
  },
  /** SPENDS MONEY: one model call over the pending edits. Proposes rules; applies none. */
  distillSocialVoice(): Promise<{ ok: boolean; proposed: VoiceLessonView[]; editsRead: number; costUsd: number; error?: string }> {
    return request("/api/social/voice/distill", { method: "POST" });
  },
  decideVoiceLesson(id: number, patch: { status?: VoiceLessonView["status"]; rule?: string }): Promise<{ ok: boolean; lesson: VoiceLessonView }> {
    return request(`/api/social/voice/lessons/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  // ── Brand-level slide defaults ────────────────────────────────────────────
  slideDefaults():Promise<{ defaults: Record<string, Record<string, number | string | boolean>> }> {
    return request("/api/social/slide-defaults");
  },
  saveSlideDefaults(
    defaults: Record<string, Record<string, number | string | boolean>>,
  ): Promise<{ ok: boolean; defaults: Record<string, Record<string, number | string | boolean>> }> {
    return request("/api/social/slide-defaults", { method: "PUT", body: JSON.stringify(defaults) });
  },
  /** A MOCK slide at the given settings — an iframe src, not something to fetch. */
  slideDefaultsPreviewUrl(o: {
    format: string; mock: string; index: number; total: number; safe: boolean;
    scale: Record<string, number | string | boolean>;
  }): string {
    const q = new URLSearchParams({
      format: o.format, mock: o.mock,
      index: String(o.index), total: String(o.total),
      safe: o.safe ? "1" : "0",
      // The UNSAVED draft travels whole, so the sliders move the picture before
      // anything is written to the brand file.
      scale: JSON.stringify(o.scale),
    });
    return `/api/social/slide-defaults/preview?${q.toString()}`;
  },

  socialCalendar(o: { from: string; to: string; platform?: string; status?: string }): Promise<{
    from: string; to: string; timezone: string; slots: CalendarSlotView[];
    /** Stretches closed to posting (Shabbat, chag, memorial days) overlapping the range. */
    blackouts: BlackoutWindowView[];
    /** Hebrew date and holidays for each Israel day in the range. */
    days: HebrewDayView[];
    /** The Postiz read-back that ran before this calendar was drawn. */
    postizSync: PostizSyncView | null;
    /**
     * What POSTIZ holds in the same window, minus anything of ours already
     * shown as a slot. Its own vocabulary, deliberately not translated into
     * ours: a Postiz DRAFT is not one of our unapproved variants.
     */
    postiz: {
      connected: boolean; error: string | null; posts: PostizPostView[];
      /** Base of the Postiz UI, for `/p/<id>` and `/launches`. Null if unset. */
      appUrl: string | null;
    };
  }> {
    const q = new URLSearchParams({ from: o.from, to: o.to });
    if (o.platform) q.set("platform", o.platform);
    if (o.status) q.set("status", o.status);
    return request(`/api/social/calendar?${q.toString()}`);
  },
  /**
   * Re-write a pack's unsent posts under today's prompt and Hebrew rules
   * (`rewrite`), or add formats it never had (`complete`). Runs in the background.
   */
  refreshSocialPack(packId: string, mode: "complete" | "rewrite", formats?: string[]): Promise<{ ok: boolean; started: true; job: PackRefreshJobView }> {
    return request(`/api/social/packs/${packId}/refresh`, { method: "POST", body: JSON.stringify({ mode, ...(formats ? { formats } : {}) }) });
  },
  packRefreshStatus(packId: string): Promise<{ job: PackRefreshJobView | null }> {
    return request(`/api/social/packs/${packId}/refresh-status`);
  },
  /** Upcoming holiday greetings and whether each already has its draft. */
  socialGreetings(): Promise<{ occasions: GreetingView[]; leadDays: number }> {
    return request("/api/social/greetings");
  },
  /** Draft one greeting now (one image + one short caption, ~$0.05). */
  createGreeting(key: string): Promise<{ ok: boolean; packId?: string; error?: string; costUsd: number }> {
    return request(`/api/social/greetings/${key}/create`, { method: "POST", body: JSON.stringify({}) });
  },
  /** The social planner: the open proposal, the learning metrics, and the fill job it shares. */
  socialPlanner(): Promise<PlannerStateView> {
    return request("/api/social/planner");
  },
  proposePlan(until: string): Promise<{ proposalId: string | null; weeks: PlannerWeekSummaryView[]; postizError: string | null; proposal: PlannerProposalView | null }> {
    return request("/api/social/planner/propose", { method: "POST", body: JSON.stringify({ until }) });
  },
  decidePlannerPick(id: number, decision: "approved" | "removed" | "pending"): Promise<{ ok: boolean }> {
    return request(`/api/social/planner/picks/${id}`, { method: "POST", body: JSON.stringify({ decision }) });
  },
  replacePlannerPick(id: number, candidateId: string): Promise<{ ok: boolean; newId?: number }> {
    return request(`/api/social/planner/picks/${id}/replace`, { method: "POST", body: JSON.stringify({ candidateId }) });
  },
  realizePlan(proposalId: string): Promise<{ ok: boolean; job?: BackfillJobView }> {
    return request(`/api/social/planner/proposals/${proposalId}/realize`, { method: "POST", body: JSON.stringify({}) });
  },
  closePlan(proposalId: string): Promise<{ ok: boolean }> {
    return request(`/api/social/planner/proposals/${proposalId}/close`, { method: "POST", body: JSON.stringify({}) });
  },
  /** Upcoming seasonal moments with the articles and backlog keywords that fit them. Read-only. */
  contentRadar(days = 120): Promise<{ moments: RadarMomentView[]; writeLeadDays: number }> {
    return request(`/api/social/radar?days=${days}`);
  },
  /** Posts still going out whose time falls inside a blackout, with a suggested new time each. */
  blackoutConflicts(days = 60): Promise<{ conflicts: BlackoutConflictView[]; reviews: CalendarReviewView[]; job: BlackoutResolveJobView | null }> {
    return request(`/api/social/blackout-conflicts?days=${days}`);
  },
  /** Moves the picked posts to their suggested time (same hour, next allowed day). Background job. */
  resolveBlackoutConflicts(variantIds: number[]): Promise<{ job: BlackoutResolveJobView }> {
    return request("/api/social/blackout-conflicts/resolve", { method: "POST", body: JSON.stringify({ variantIds }) });
  },
  blackoutResolveStatus(): Promise<{ job: BlackoutResolveJobView | null }> {
    return request("/api/social/blackout-conflicts/status");
  },
  /** "רענון מפוסטיז": read Postiz back now. Writes nothing to Postiz. */
  reconcilePostiz(): Promise<PostizSyncView> {
    return request("/api/social/postiz/reconcile", { method: "POST", body: JSON.stringify({}) });
  },
  /**
   * `all` (the default) — the three content sources in one list, each row
   * tagged with `source`. `platform` — articles the platform produced.
   * `sanity-blog` — live blog posts it did not (older ones).
   * `sanity-treatment` — treatment-method pages. `spotlight` is its own pass:
   * a run carrying one is forced to a 7-day spacing, so it cannot be mixed.
   */
  backfillCandidates(source: BackfillSourceView | "all" = "all"): Promise<{ candidates: BackfillCandidateView[]; packCostUsd: number }> {
    return request(`/api/social/backfill/candidates?source=${source}`);
  },
  /** Omit `apply` for a dry run. Nothing is written and nothing is spent. */
  backfill(body: {
    keywordIds: string[]; startIso?: string; spacingDays?: number; generateMissing?: boolean;
    /** Fill with these formats only. Omit for every format each article supports. */
    formats?: string[];
    apply?: boolean;
  }): Promise<BackfillPreviewView | { dryRun: false; started: true; job: BackfillJobView }> {
    return request("/api/social/backfill", { method: "POST", body: JSON.stringify(body) });
  },
  /**
   * The current or most recent fill. Applying runs in the background because
   * generating packs takes minutes and the gateway hangs up at 60 seconds.
   */
  backfillStatus(): Promise<{ job: BackfillJobView | null }> {
    return request("/api/social/backfill/status");
  },

  /**
   * `style` defaults to `explainer` SERVER-SIDE, and this signature keeps it
   * optional so a caller cannot get the deck by forgetting a field. The deck is
   * free and instant but was judged as video and does not hold attention.
   */
  startSocialVideos(
    keywordId: string,
    opts: {
      packId?: string; style?: "explainer" | "deck"; targetSeconds?: number; reuseFootage?: boolean;
      /** GATE 1. The explainer refuses without an approved plan. */
      storyboardId?: number;
    } = {},
  ): Promise<{ ok: boolean; job: SocialVideoJob }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(keywordId)}/social-videos`, {
      method: "POST",
      body: JSON.stringify(opts),
    });
  },
  // ── Storyboards ───────────────────────────────────────────────────────────
  // Planning costs one LLM call and buys nothing. Approving it is what unlocks
  // footage; releasing the finished reel is what unlocks publishing.

  /**
   * Work out this article's video topics, for one written before extraction
   * existed. About a cent. Without them the shot writer reads the whole piece
   * and its plan tends to become a summary of the article.
   */
  extractVideoTopics(keywordId: string): Promise<{ ok: boolean; topics: VideoTopicView[]; warnings: string[]; costUsd: number }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(keywordId)}/video-topics`, { method: "POST" });
  },
  storyboards(keywordId: string): Promise<{ storyboards: StoryboardView[]; topics: VideoTopicView[] }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(keywordId)}/storyboards`);
  },
  /**
   * Start planning a reel. Returns at once with a job: a plan takes 90-140s and
   * the gateway hangs up at 60, so the plan itself arrives through
   * `planStoryboardStatus`. A second start for the same article is a 409 that
   * carries the running job.
   */
  planStoryboard(
    keywordId: string,
    opts: { topicId?: string; shotCount?: number; shotSeconds?: number } = {},
  ): Promise<{ ok: boolean; started: boolean; job: StoryboardPlanJobView }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(keywordId)}/storyboards`, {
      method: "POST",
      body: JSON.stringify(opts),
    });
  },
  planStoryboardStatus(keywordId: string): Promise<{ job: StoryboardPlanJobView | null }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(keywordId)}/storyboards/plan-status`);
  },
  /**
   * Shorten or fix ONE narration line, before the plan is approved. The server
   * applies the same repairs and checks a generated line gets, so a hand edit
   * cannot smuggle in a calque or a number the voice reads wrong.
   */
  editStoryboardNarration(id: number, shotIndex: number, text: string): Promise<{
    ok: boolean; storyboard: StoryboardView; notes: string[]; issues: { rule: string; message: string }[];
  }> {
    return request(`/api/social/storyboards/${id}/narration`, {
      method: "PATCH",
      body: JSON.stringify({ shotIndex, text }),
    });
  },
  approveStoryboard(id: number): Promise<{ storyboard: StoryboardView }> {
    return request(`/api/social/storyboards/${id}/approve`, { method: "POST" });
  },
  /**
   * Back to draft so narration can be edited. Re-approval is required, so this
   * re-opens gate 1 rather than skipping it.
   */
  unapproveStoryboard(id: number): Promise<{ storyboard: StoryboardView }> {
    return request(`/api/social/storyboards/${id}/unapprove`, { method: "POST" });
  },
  rejectStoryboard(id: number, reason: string): Promise<{ storyboard: StoryboardView }> {
    return request(`/api/social/storyboards/${id}/reject`, { method: "POST", body: JSON.stringify({ reason }) });
  },
  releaseStoryboard(id: number): Promise<{ storyboard: StoryboardView }> {
    return request(`/api/social/storyboards/${id}/release`, { method: "POST" });
  },

  /** `status: "idle"` when no render has ever been started for this article. */
  socialVideoStatus(keywordId: string): Promise<SocialVideoJob> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(keywordId)}/social-videos-status`);
  },

  /** Returns HTML, so it is an iframe src rather than something to fetch. */
  socialSlideUrl(o: {
    variantId: number; index: number; format?: string; safe?: boolean;
    scale?: Record<string, number | string>;
  }): string {
    const q = new URLSearchParams({
      variantId: String(o.variantId),
      index: String(o.index),
      format: o.format ?? "portrait",
      safe: o.safe ? "1" : "0",
    });
    for (const [k, v] of Object.entries(o.scale ?? {})) {
      // `logoAsset` is a choice, not a multiplier — it travels under its own
      // name rather than being mangled into `sLogoAsset`.
      if (k === "logoAsset") { if (v) q.set("logoAsset", String(v)); continue; }
      // `lift` is pixels, so its neutral value is 0, not 1. Sending it through
      // the `!== 1` test below would drop a 1px lift and keep a 0px one.
      if (k === "lift") { if (Number(v) > 0) q.set("sLift", String(v)); continue; }
      if (v !== 1) q.set(`s${k[0]!.toUpperCase()}${k.slice(1)}`, String(v));
    }
    return `/api/social/slide?${q.toString()}`;
  },

  listSpotlightTherapists(): Promise<SpotlightTherapistsResponse> {
    return request("/api/spotlight/therapists");
  },
  getSpotlightPayload(slug: string, campaign?: string | null): Promise<{ payload: SpotlightPayload; carousel: string[]; exportable: string[]; dropped: { slide: string; reason: string }[]; coverApproved: boolean | null }> {
    return request(`/api/spotlight/payload/${encodeURIComponent(slug)}${campaign ? `?campaign=${encodeURIComponent(campaign)}` : ""}`);
  },
  /** The slide route returns HTML, so it is used as an iframe src, not fetched. */
  spotlightSlideUrl(o: {
    slug: string; slide: string; format: string; theme: string;
    safe?: boolean; campaign?: string | null; scale?: Record<string, number>;
  }): string {
    const q = new URLSearchParams({ slug: o.slug, slide: o.slide, format: o.format, theme: o.theme, safe: o.safe ? "1" : "0" });
    if (o.campaign) q.set("campaign", o.campaign);
    for (const [k, v] of Object.entries(o.scale ?? {})) {
      if (v !== 1) q.set(`s${k[0]!.toUpperCase()}${k.slice(1)}`, String(v));
    }
    return `/api/spotlight/slide?${q.toString()}`;
  },
  createSpotlightCampaign(slug: string): Promise<{ ok: boolean; detail: CampaignDetail }> {
    return request("/api/spotlight/campaigns", { method: "POST", body: JSON.stringify({ slug }) });
  },
  getSpotlightCampaign(id: string): Promise<CampaignDetail> {
    return request(`/api/spotlight/campaigns/${encodeURIComponent(id)}`);
  },
  listSpotlightCampaigns(status?: string): Promise<{ items: SpotlightCampaign[] }> {
    return request(`/api/spotlight/campaigns${status ? `?status=${encodeURIComponent(status)}` : ""}`);
  },
  editSpotlightField(id: string, field: string, value: string | null): Promise<{ ok: boolean; forked: boolean; detail: CampaignDetail }> {
    return request(`/api/spotlight/campaigns/${encodeURIComponent(id)}/field`, { method: "PATCH", body: JSON.stringify({ field, value }) });
  },
  sendSpotlightWhatsapp(id: string, dryRun = false): Promise<{ ok: boolean; dryRun?: boolean; result?: { to?: string | null; templateName?: string; messageId?: string; error?: string; payload?: unknown }; detail?: CampaignDetail; error?: string }> {
    return request(`/api/spotlight/campaigns/${encodeURIComponent(id)}/send-whatsapp`, { method: "POST", body: JSON.stringify({ dryRun }) });
  },
  approveSpotlightField(id: string, field: string, approved: boolean): Promise<{ ok: boolean; detail: CampaignDetail }> {
    return request(`/api/spotlight/campaigns/${encodeURIComponent(id)}/approve`, { method: "POST", body: JSON.stringify({ field, approved }) });
  },
  generateSpotlightCopy(id: string): Promise<{ ok: boolean; detail: CampaignDetail }> {
    return request(`/api/spotlight/campaigns/${encodeURIComponent(id)}/generate-copy`, { method: "POST" });
  },
  saveSpotlightRenderSettings(id: string, body: { slideOrder?: string[]; theme?: string; format?: string; sizeScale?: Record<string, number> }): Promise<{ ok: boolean }> {
    return request(`/api/spotlight/campaigns/${encodeURIComponent(id)}/render-settings`, { method: "PUT", body: JSON.stringify(body) });
  },
  exportSpotlight(id: string, body: { format?: string; theme?: string; imageFormat?: string; quality?: number; density?: 1 | 2; slides?: string[] }): Promise<{
    ok: boolean; dir: string;
    files: { slide: string; path: string; bytes: number }[];
    dropped: { slide: string; reason: string }[];
  }> {
    return request(`/api/spotlight/campaigns/${encodeURIComponent(id)}/export`, { method: "POST", body: JSON.stringify(body) });
  },
  sendSpotlightFeedback(id: string, body: string): Promise<{ ok: boolean; analysis: { intent: string; summaryHe: string; needsProfileEdit: boolean; profileEditHe: string | null }; deferred: { field: string; url: string | null; reason: string }[]; detail: CampaignDetail }> {
    return request(`/api/spotlight/campaigns/${encodeURIComponent(id)}/feedback`, { method: "POST", body: JSON.stringify({ body }) });
  },
  /** The manual approval request: message + WhatsApp link, or why it cannot go yet. */
  spotlightManualRequest(id: string): Promise<{ message: string | null; waLink: string | null; profileUrl: string; blockers: string[] }> {
    return request(`/api/spotlight/campaigns/${encodeURIComponent(id)}/manual-request`);
  },
  /** "I sent it" — refused on the server while anything blocks asking. */
  markSpotlightSent(id: string): Promise<{ ok: boolean; detail: CampaignDetail }> {
    return request(`/api/spotlight/campaigns/${encodeURIComponent(id)}/sent`, { method: "POST", body: JSON.stringify({ channel: "manual" }) });
  },
  /** A slide as a PNG download. A plain URL: used as an <a download> href. */
  spotlightPreviewPngUrl(id: string, slide = "cover"): string {
    return `/api/spotlight/campaigns/${encodeURIComponent(id)}/preview.png?slide=${encodeURIComponent(slide)}`;
  },
  listKeywordAssets(id: string): Promise<{ ok: boolean; assets: KeywordAssetView[]; countsByStatus: Record<string, number> }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/assets`);
  },
  /**
   * Import an externally-produced video (e.g. a Maestro render) as a keyword
   * video asset. Multipart upload — bypasses the JSON `request()` helper.
   */
  async importKeywordVideo(id: string, file: File, kind: string): Promise<{ ok: boolean; kind?: string; status?: string; asset?: KeywordAssetView; error?: string }> {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(
      `/api/pipeline/keywords/${encodeURIComponent(id)}/import-video?kind=${encodeURIComponent(kind)}`,
      { method: "POST", body: fd },
    );
    const json = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
    if (!res.ok || json.ok === false) throw new Error(json.error ?? `HTTP ${res.status}`);
    return json as { ok: boolean };
  },
  /** Regenerate images. mode "all" = every image; "selective" = only the picked roles/sections. */
  regenerateImages(
    id: string,
    body: { mode: "all" | "selective" | "missing"; hero?: boolean; social?: boolean; thumbnail?: boolean; sections?: number[]; promptOverrides?: Record<string, string> },
  ): Promise<ActionResult & { started?: boolean; mode?: string }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/images`, { method: "POST", body: JSON.stringify(body) });
  },
  /**
   * Forward-flow "Generate images": fill only the MISSING images (hero, in-content
   * per {{IMAGE:}} marker, social) for a produced article. Cheap + idempotent —
   * skips anything already generated, so it's safe to click after Produce or to
   * re-run. Background job; poll getImagesStatus for progress.
   */
  generateImages(id: string): Promise<ActionResult & { started?: boolean; mode?: string }> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/images`, { method: "POST", body: JSON.stringify({ mode: "missing" }) });
  },
  getImagesStatus(id: string): Promise<ImageJobView> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/images-status`);
  },

  // ── Errors (pipeline_errors) ──────────────────────────────────────────────
  listErrors(filters: ErrorListFilters = {}): Promise<{ ok: boolean; errors: PipelineErrorView[]; returned: number }> {
    const qs = new URLSearchParams();
    if (filters.unresolvedOnly !== undefined) qs.set("unresolvedOnly", String(filters.unresolvedOnly));
    if (filters.level) qs.set("level", filters.level);
    if (filters.operation) qs.set("operation", filters.operation);
    if (filters.operationPrefix) qs.set("operationPrefix", filters.operationPrefix);
    if (filters.keywordId) qs.set("keywordId", filters.keywordId);
    if (filters.since) qs.set("since", filters.since);
    if (filters.limit) qs.set("limit", String(filters.limit));
    const suffix = qs.toString();
    return request(`/api/errors${suffix ? `?${suffix}` : ""}`);
  },
  errorCounts(): Promise<{ ok: boolean; unresolved: { error: number; warning: number; info: number }; resolved: { error: number; warning: number; info: number }; unresolvedTotal: number }> {
    return request(`/api/errors/counts`);
  },
  topErrorOperations(): Promise<{ ok: boolean; since: string; operations: { operation: string; n: number }[] }> {
    return request(`/api/errors/top-operations`);
  },
  resolveError(id: number, body: { by?: string; note?: string } = {}): Promise<{ ok: boolean; error: PipelineErrorView }> {
    return request(`/api/errors/${id}/resolve`, { method: "POST", body: JSON.stringify(body) });
  },

  // ── Cost ledger ───────────────────────────────────────────────────────────
  costSummary(params: { since?: string; until?: string } = {}): Promise<CostSummaryResponse> {
    const qs = new URLSearchParams();
    if (params.since) qs.set("since", params.since);
    if (params.until) qs.set("until", params.until);
    const suffix = qs.toString();
    return request(`/api/cost/summary${suffix ? `?${suffix}` : ""}`);
  },
  costTimeseries(params: { since?: string; until?: string; bucket?: "day" | "hour" } = {}): Promise<CostTimeseriesResponse> {
    const qs = new URLSearchParams();
    if (params.since) qs.set("since", params.since);
    if (params.until) qs.set("until", params.until);
    if (params.bucket) qs.set("bucket", params.bucket);
    const suffix = qs.toString();
    return request(`/api/cost/timeseries${suffix ? `?${suffix}` : ""}`);
  },
  costRecent(params: { limit?: number; provider?: string; operationPrefix?: string } = {}): Promise<{ ok: boolean; rows: CostLedgerView[] }> {
    const qs = new URLSearchParams();
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.provider) qs.set("provider", params.provider);
    if (params.operationPrefix) qs.set("operationPrefix", params.operationPrefix);
    const suffix = qs.toString();
    return request(`/api/cost/recent${suffix ? `?${suffix}` : ""}`);
  },
  awsBedrockCost(days = 30): Promise<AwsBedrockCostResponse> {
    return request(`/api/cost/aws-bedrock?days=${days}`);
  },
  costForKeyword(id: string): Promise<{ ok: boolean; keywordId: string; totalUsd: number; rows: { provider: string; operation: string; model: string | null; totalUsd: number; rows: number }[] }> {
    return request(`/api/cost/keyword/${encodeURIComponent(id)}`);
  },

  // ── Admin (backups, ops) ──────────────────────────────────────────────────
  backupStatus(): Promise<BackupStatusResponse> {
    return request(`/api/admin/backup`);
  },
  backupRun(): Promise<{ ok: boolean; record?: { filename: string; bytes: number; createdAt: string }; prunedCount: number; durationMs: number; error?: string }> {
    return request(`/api/admin/backup/run`, { method: "POST" });
  },
  backupPrune(): Promise<{ ok: boolean; deleted: number }> {
    return request(`/api/admin/backup/prune`, { method: "POST" });
  },
  // Operator-data export is a file download — link straight to this URL.
  operatorDataExportUrl: `/api/admin/operator-data/export`,
  // Full database snapshot (every tab's structured data) — also a direct download.
  dbSnapshotUrl: `/api/admin/db-snapshot`,
  operatorDataImport(data: unknown): Promise<{
    ok: boolean;
    error?: string;
    report?: { articleKeywords: { matched: number; updated: number; unmatched: string[] }; alwaysLinks: { upserted: number } };
  }> {
    return request(`/api/admin/operator-data/import`, { method: "POST", body: JSON.stringify(data) });
  },
  // Full planning-data sync (clusters + memberships + entities + metrics + always-links).
  planningDataExportUrl: `/api/admin/planning-data/export`,
  // The generated-media layer: library clips and storyboards. A separate sync
  // from the planning one because that endpoint promises never to touch runtime
  // data, and this is runtime data.
  mediaCatalogueExportUrl: `/api/admin/media-catalogue/export`,
  planningDataImport(data: unknown, opts?: { dryRun?: boolean }): Promise<{
    ok: boolean;
    error?: string;
    report?: {
      dryRun: boolean;
      clusters: { created: number; updated: number };
      keywords: { created: number; updated: number };
      clusterPrimary: { set: number; skipped: number };
      entities: { created: number; updated: number };
      keywordMetrics: { upserted: number };
      alwaysLinks: { upserted: number };
    };
  }> {
    return request(`/api/admin/planning-data/import${opts?.dryRun ? "?dryRun=1" : ""}`, { method: "POST", body: JSON.stringify(data) });
  },
  mediaCatalogueImport(
    data: unknown,
    opts?: { dryRun?: boolean },
  ): Promise<{ ok: boolean; error?: string; report?: {
    clipsInserted: number; clipsUpdated: number;
    storyboardsInserted: number; storyboardsUpdated: number;
    skipped: string[]; dryRun: boolean;
  } }> {
    return request(`/api/admin/media-catalogue/import${opts?.dryRun ? "?dryRun=1" : ""}`, { method: "POST", body: JSON.stringify(data) });
  },

  // ── Pipeline (produce — Server-Sent Events) ───────────────────────────────
  /**
   * Streams article-production events. Returns an unsubscribe function.
   * Events: `start`, `section_done`, `done`, `error`.
   */
  produceArticle(
    id: string,
    body: { authorId?: string; model?: string; withMeta?: boolean; allowDuplicate?: boolean },
    handlers: {
      onStart?: (data: ProduceStartEvent) => void;
      onSection?: (data: ProduceSectionEvent) => void;
      onDone?: (data: ProduceDoneEvent) => void;
      onError?: (error: string) => void;
      /** A live page already covers this keyword — the run never started. */
      onDuplicate?: (info: DuplicateInfo, message: string) => void;
    },
  ): () => void {
    const controller = new AbortController();
    void streamProduce(id, body, handlers, controller.signal);
    return () => controller.abort();
  },

  /** Persistent produce-job progress (survives refresh / reopen). */
  getProduceStatus(id: string): Promise<ProduceJobView> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/produce-status`);
  },
  getViralStatus(id: string): Promise<ViralJobView> {
    return request(`/api/pipeline/keywords/${encodeURIComponent(id)}/viral-status`);
  },

  // ── Site audit ──────────────────────────────────────────────────────────────
  runAudit(body: { source?: AuditSourceView; maxPages?: number } = {}): Promise<ActionResult> {
    return request(`/api/audit/run`, { method: "POST", body: JSON.stringify(body) });
  },
  getAuditStatus(): Promise<AuditJobView> {
    return request(`/api/audit/status`);
  },
  getAuditLatest(): Promise<{ run: AuditRunView | null; findings: AuditFindingView[] }> {
    return request(`/api/audit/latest`);
  },
  listAuditRuns(): Promise<{ runs: AuditRunView[] }> {
    return request(`/api/audit/runs`);
  },
  getAuditRun(id: number): Promise<{ run: AuditRunView | null; findings: AuditFindingView[] }> {
    return request(`/api/audit/runs/${id}`);
  },

  // ── Brand kit ─────────────────────────────────────────────────────────────
  getBrandManifest(): Promise<BrandManifest> {
    return request("/api/brand/manifest");
  },
  putBrandFile(body: { path: string; content: string }): Promise<{ ok: boolean; path: string; sizeBytes: number; reloaded: boolean; error?: string }> {
    return request("/api/brand/files", { method: "PUT", body: JSON.stringify(body) });
  },
  reloadBrand(): Promise<{ ok: boolean; reloadedAt: string }> {
    return request("/api/brand/reload", { method: "POST" });
  },
  uploadBrandAsset(body: { name: string; contentBase64: string }): Promise<{ ok: boolean; asset?: BrandAssetEntry; error?: string }> {
    return request("/api/brand/assets", { method: "POST", body: JSON.stringify(body) });
  },
  deleteBrandAsset(name: string): Promise<{ ok: boolean; deleted: string }> {
    return request(`/api/brand/assets/${encodeURIComponent(name)}`, { method: "DELETE" });
  },

  // ── Entity Intelligence Hub ─────────────────────────────────────────────────
  getEntitiesOverview(): Promise<EntitiesOverview> {
    return request("/api/entities/overview");
  },
  listEntities(filters: EntityListFilters = {}): Promise<{ count: number; items: EntityView[] }> {
    const qs = new URLSearchParams();
    if (filters.entityType) qs.set("entityType", filters.entityType);
    if (filters.category) qs.set("category", filters.category);
    if (filters.excludeFromContent !== undefined) qs.set("excludeFromContent", String(filters.excludeFromContent));
    if (filters.existingRelationship !== undefined) qs.set("existingRelationship", String(filters.existingRelationship));
    if (filters.q) qs.set("q", filters.q);
    return request(`/api/entities${qs.size ? `?${qs.toString()}` : ""}`);
  },
  getEntity(id: string): Promise<EntityDetailView> {
    return request(`/api/entities/${encodeURIComponent(id)}`);
  },
  createEntity(body: EntityCreateBody): Promise<{ ok: boolean; entity: EntityView }> {
    return request("/api/entities", { method: "POST", body: JSON.stringify(body) });
  },
  patchEntity(id: string, body: Partial<EntityCreateBody>): Promise<{ ok: boolean; entity: EntityView }> {
    return request(`/api/entities/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  deleteEntity(id: string): Promise<{ ok: boolean }> {
    return request(`/api/entities/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  addEntityCategory(id: string, category: string): Promise<{ ok: boolean; detail: EntityDetailView }> {
    return request(`/api/entities/${encodeURIComponent(id)}/categories`, { method: "POST", body: JSON.stringify({ category }) });
  },
  removeEntityCategory(id: string, category: string): Promise<{ ok: boolean; detail: EntityDetailView }> {
    return request(`/api/entities/${encodeURIComponent(id)}/categories/${encodeURIComponent(category)}`, { method: "DELETE" });
  },
  addEntityTag(id: string, tag: string): Promise<{ ok: boolean; detail: EntityDetailView }> {
    return request(`/api/entities/${encodeURIComponent(id)}/tags`, { method: "POST", body: JSON.stringify({ tag }) });
  },
  removeEntityTag(id: string, tag: string): Promise<{ ok: boolean; detail: EntityDetailView }> {
    return request(`/api/entities/${encodeURIComponent(id)}/tags/${encodeURIComponent(tag)}`, { method: "DELETE" });
  },
  addEntityAlias(id: string, aliasText: string, language?: "he" | "en" | null): Promise<{ ok: boolean; detail: EntityDetailView }> {
    return request(`/api/entities/${encodeURIComponent(id)}/aliases`, { method: "POST", body: JSON.stringify({ aliasText, language }) });
  },
  removeEntityAlias(id: string, aliasText: string): Promise<{ ok: boolean; detail: EntityDetailView }> {
    return request(`/api/entities/${encodeURIComponent(id)}/aliases`, { method: "DELETE", body: JSON.stringify({ aliasText }) });
  },
  addEntitySocialHandle(id: string, body: { platform: string; handle: string; url?: string | null; verified?: boolean; isPrimary?: boolean }): Promise<{ ok: boolean; detail: EntityDetailView }> {
    return request(`/api/entities/${encodeURIComponent(id)}/social-handles`, { method: "POST", body: JSON.stringify(body) });
  },
  patchEntitySocialHandle(id: string, hid: number, body: { url?: string | null; verified?: boolean; isPrimary?: boolean }): Promise<{ ok: boolean; detail: EntityDetailView }> {
    return request(`/api/entities/${encodeURIComponent(id)}/social-handles/${hid}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  removeEntitySocialHandle(id: string, hid: number): Promise<{ ok: boolean; detail: EntityDetailView }> {
    return request(`/api/entities/${encodeURIComponent(id)}/social-handles/${hid}`, { method: "DELETE" });
  },
  addEntityContact(id: string, body: { kind: string; value: string; isPrimary?: boolean }): Promise<{ ok: boolean; detail: EntityDetailView }> {
    return request(`/api/entities/${encodeURIComponent(id)}/contacts`, { method: "POST", body: JSON.stringify(body) });
  },
  removeEntityContact(id: string, cid: number): Promise<{ ok: boolean; detail: EntityDetailView }> {
    return request(`/api/entities/${encodeURIComponent(id)}/contacts/${cid}`, { method: "DELETE" });
  },
  addEntityRelationship(id: string, targetId: string, kind: EntityRelationshipKindView): Promise<{ ok: boolean; detail: EntityDetailView }> {
    return request(`/api/entities/${encodeURIComponent(id)}/relationships`, { method: "POST", body: JSON.stringify({ targetId, kind }) });
  },
  removeEntityRelationship(id: string, rid: number): Promise<{ ok: boolean; detail: EntityDetailView }> {
    return request(`/api/entities/${encodeURIComponent(id)}/relationships/${rid}`, { method: "DELETE" });
  },
  entityImportPreview(csvContent: string): Promise<EntityImportPreviewResponse> {
    return request("/api/entities/import/preview", { method: "POST", body: JSON.stringify({ csvContent }) });
  },
  entityImportCommit(csvContent: string, mappings: EntityColumnMappingView[]): Promise<EntityImportCommitResponse> {
    return request("/api/entities/import/commit", { method: "POST", body: JSON.stringify({ csvContent, mappings }) });
  },
  discoverEntities(body: { clusterId?: string; classify?: boolean } = {}): Promise<EntityDiscoverResponse> {
    return request("/api/entities/discover", { method: "POST", body: JSON.stringify(body) });
  },
  listEntitySuggestions(status?: EntitySuggestionStatusView): Promise<EntitySuggestionsResponse> {
    const qs = status ? `?status=${status}` : "";
    return request(`/api/entities/suggestions${qs}`);
  },
  regroupEntitySuggestions(useLlm = true): Promise<{ ok: boolean; summary: string; scanned: number; linkedDeterministic: number; linkedLlm: number; mergedPending: number; renamed: number; links: { domain: string; entityName: string }[]; warnings: string[] }> {
    return request(`/api/entities/suggestions/regroup`, { method: "POST", body: JSON.stringify({ useLlm }) });
  },
  renameEntitySuggestion(id: string, candidateName: string): Promise<{ ok: boolean; suggestion: EntitySuggestionView }> {
    return request(`/api/entities/suggestions/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ candidateName }) });
  },
  acceptEntitySuggestion(id: string, categories?: EntityCategoryNameView[]): Promise<{ ok: boolean; entityId: string; suggestion: EntitySuggestionView }> {
    return request(`/api/entities/suggestions/${encodeURIComponent(id)}/accept`, { method: "POST", body: JSON.stringify(categories ? { categories } : {}) });
  },
  bulkEntitySuggestions(ids: string[], action: "accept" | "reject" | "reopen"): Promise<{ ok: boolean; succeeded: number; failed: number; errors: { id: string; error: string }[] }> {
    return request(`/api/entities/suggestions/bulk`, { method: "POST", body: JSON.stringify({ ids, action }) });
  },
  rejectEntitySuggestion(id: string, notes?: string): Promise<{ ok: boolean; suggestion: EntitySuggestionView }> {
    return request(`/api/entities/suggestions/${encodeURIComponent(id)}/reject`, { method: "POST", body: JSON.stringify({ notes }) });
  },
  reopenEntitySuggestion(id: string): Promise<{ ok: boolean; suggestion: EntitySuggestionView }> {
    return request(`/api/entities/suggestions/${encodeURIComponent(id)}/reopen`, { method: "POST" });
  },
  getEntityWatchedChannels(id: string): Promise<{ channels: RssFeedView[] }> {
    return request(`/api/entities/${encodeURIComponent(id)}/watched-channels`);
  },
  watchEntityYoutube(id: string, body: { input: string; name?: string | null; autoAnalyze?: boolean }): Promise<WatchChannelResult> {
    return request(`/api/entities/${encodeURIComponent(id)}/watch-youtube`, { method: "POST", body: JSON.stringify(body) });
  },
  unwatchEntityChannel(id: string, feedId: string): Promise<{ ok: boolean }> {
    return request(`/api/entities/${encodeURIComponent(id)}/watched-channels/${encodeURIComponent(feedId)}`, { method: "DELETE" });
  },
};

export interface WatchChannelResult {
  ok: boolean;
  feed: RssFeedView;
  resolved: { channelId: string; channelTitle: string | null; uploadsFeedUrl: string; channelUrl: string };
  alreadyWatched: boolean;
  /** First-add backfill poll result (null when already watched or poll skipped). */
  poll: { ok: boolean; itemsSeen: number; itemsNew: number; itemsAnalyzed: number } | null;
}

// ── Entity Intelligence Hub types ─────────────────────────────────────────────

export type EntityTypeView = "individual" | "organization" | "product" | "platform" | "publication";

export const ENTITY_CATEGORY_NAMES = [
  "direct_competitor", "peer", "partnership_potential", "affiliate_candidate",
  "influencer_potential", "outreach_potential", "do_not_mention", "recommended",
  "institution", "school", "professional_union", "regulator", "media_outlet",
] as const;
export type EntityCategoryNameView = (typeof ENTITY_CATEGORY_NAMES)[number];

export const ENTITY_SOCIAL_PLATFORMS = [
  "youtube", "instagram", "tiktok", "x", "facebook", "linkedin", "pinterest", "threads", "spotify",
] as const;
export type EntitySocialPlatformView = (typeof ENTITY_SOCIAL_PLATFORMS)[number];

export interface EntityView {
  id: string;
  canonicalName: string;
  displayNameEn: string | null;
  displayNameHe: string | null;
  entityType: EntityTypeView;
  primaryUrl: string | null;
  domain: string | null;
  description: string | null;
  notes: string | null;
  firstSeenAt: string;
  firstSource: string | null;
  manuallyVerified: boolean;
  excludeFromContent: boolean;
  excludeReason: string | null;
  excludeExceptions: string[];
  isExistingAlternabeRelationship: boolean;
  alternabeRelationshipKind: string | null;
  alternabeInternalId: string | null;
  hasLivePage: boolean;
  serpVisibilityScore: number | null;
  overallSeoScore: number | null;
  influencerScore: number | null;
  /** Primary contact, attached by the list endpoint (for outreach-facing views). */
  primaryEmail?: string | null;
  primaryPhone?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EntityCategoryView { id: number; entityId: string; category: EntityCategoryNameView; source: string; confidence: number | null; setAt: string; }
export interface EntityTagView { id: number; entityId: string; tag: string; source: string; createdAt: string; }
export interface EntitySourceView { id: number; entityId: string; sourceType: string; sourceRef: string | null; seenAt: string; snippet: string | null; }
export interface EntityAliasView { id: number; entityId: string; aliasText: string; language: string | null; source: string; }
export interface EntitySocialHandleView { id: number; entityId: string; platform: EntitySocialPlatformView; handle: string; url: string | null; verified: boolean; isPrimary: boolean; }
export interface EntityContactView { id: number; entityId: string; kind: string; value: string; isPrimary: boolean; }
export interface EntitySeoSignalView { id: number; entityId: string; fetchedAt: string; serpAppearances: number | null; organicKeywords: number | null; estimatedTraffic: number | null; domainRating: number | null; }
export interface EntityRelationshipView { id: number; parentEntityId: string; childEntityId: string; kind: string; parentName: string | null; childName: string | null; }
export interface EntityAuditView { id: number; entityId: string; action: string; summary: string | null; at: string; }

export interface EntityDetailView {
  entity: EntityView;
  categories: EntityCategoryView[];
  tags: EntityTagView[];
  sources: EntitySourceView[];
  aliases: EntityAliasView[];
  socialHandles: EntitySocialHandleView[];
  contacts: EntityContactView[];
  seoSignals: EntitySeoSignalView[];
  relationships: EntityRelationshipView[];
  audit: EntityAuditView[];
}

export type EntityRelationshipKindView = "subsidiary_of" | "parent_company_of" | "affiliated_with" | "practices_at" | "authored_by";

export interface EntitiesOverview {
  total: number;
  byType: Record<string, number>;
  excluded: number;
  relationships: number;
  byCategory: Record<string, number>;
}

export interface EntityListFilters {
  entityType?: EntityTypeView;
  category?: EntityCategoryNameView;
  excludeFromContent?: boolean;
  existingRelationship?: boolean;
  q?: string;
}

export interface EntityCreateBody {
  canonicalName: string;
  entityType: EntityTypeView;
  displayNameEn?: string | null;
  displayNameHe?: string | null;
  primaryUrl?: string | null;
  description?: string | null;
  notes?: string | null;
  excludeFromContent?: boolean;
  excludeReason?: string | null;
  excludeExceptions?: string[];
  isExistingAlternabeRelationship?: boolean;
  alternabeRelationshipKind?: string | null;
  alternabeInternalId?: string | null;
  manuallyVerified?: boolean;
}

export interface EntityColumnMappingView {
  source: string;
  target: string;
  confidence: number;
  rationale: string | null;
}

export interface EntityImportPreviewResponse {
  preview: { headers: string[]; rows: Record<string, string>[]; totalRows: number };
  mappings: EntityColumnMappingView[];
  mapperModel: string;
}

export interface EntityImportCommitResponse {
  ok: boolean;
  created: number;
  skipped: number;
  contactsAdded: number;
  tagsAdded: number;
  warnings: string[];
}

export type EntitySuggestionStatusView = "pending" | "accepted" | "rejected" | "duplicate";

export interface EntitySuggestionView {
  id: string;
  candidateName: string;
  domain: string | null;
  entityType: EntityTypeView | null;
  suggestedCategories: EntityCategoryNameView[];
  classifierConfidence: number | null;
  classifierRationale: string | null;
  sourceType: "serp_competitor" | "competitor_extract" | "discovery";
  sourceRef: string | null;
  clusterId: string | null;
  contextSnippet: string | null;
  /** When set, accepting attaches this website/name to the existing entity
   *  named by `linkToEntityName` instead of creating a new one. */
  linkToEntityId: string | null;
  linkToEntityName?: string | null;
  status: EntitySuggestionStatusView;
  reviewedNotes: string | null;
  acceptedEntityId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EntityDiscoverResponse {
  ok: boolean;
  summary: string;
  serpCandidates: number;
  extractCandidates: number;
  created: number;
  skippedExisting: number;
  llmCalls: number;
  costUsd: number;
  warnings: string[];
}

export interface EntitySuggestionsResponse {
  counts: Record<EntitySuggestionStatusView, number>;
  items: EntitySuggestionView[];
}

export interface AccuracyFinding {
  severity: "high" | "medium" | "low";
  type: "wrong_term" | "unsourced_stat" | "unsupported_claim" | "medical_overreach" | "off_topic" | "other";
  excerpt: string;
  issue: string;
  suggestion: string;
}

export interface AlwaysLinkTarget {
  id: string;
  url: string;
  mainAnchor: string;
  secondaryAnchors: string[];
  sortOrder: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SpotlightCampaign {
  id: string; therapistSlug: string; therapistName: string; providerKind: string | null;
  profileScore: number | null; status: string; revision: number;
  sentAt: string | null; respondedAt: string | null; createdAt: string; updatedAt: string;
}
export interface SpotlightFieldState {
  field: string; value: string | null;
  origin: "generated" | "therapist" | "operator";
  sourceQuote: string | null; approved: boolean;
}
export interface SpotlightVariation {
  id: string; campaignId: string; revision: number;
  source: "system" | "feedback" | "operator";
  fromFeedbackId: number | null; parentId: string | null;
  fields: SpotlightFieldState[]; createdAt: string;
}
export interface CampaignDetail {
  campaign: SpotlightCampaign;
  current: SpotlightVariation | null;
  history: SpotlightVariation[];
  awaitingApproval: string[];
  /** Cover-slide fields — the gate that unlocks approving everything else. */
  coverFields: string[];
  coverApproved: boolean;
  coverPending: string[];
  publishable: boolean;
  editable: string[];
  delivery: { status: string | null; deliveredAt: string | null; error: string | null; reached: boolean };
  /** The therapist asked not to be published. */
  optedOut: boolean;
  /** Why the therapist cannot be asked to approve yet. Empty = may ask. */
  askBlockers: string[];
  /** Social checks per caption. */
  captionIssues: { field: string; issues: SocialCheckIssue[] }[];
}

export interface SpotlightTherapist {
  slug: string; name: string; firstName: string; photoUrl: string | null;
  score: number; eligible: boolean; blockers: string[]; parts: Record<string, number>;
  kind: string; heading: string; cities: string[]; methodCount: number; optedOut: boolean;
  /** Normalised platforms the therapist already publishes on, e.g. ["facebook","instagram"]. */
  socials: string[];
  /** True when at least one of those is somewhere a post can be reshared. */
  reshareReach: boolean;
}
// ── Social packs ─────────────────────────────────────────────────────────
export type SocialPlatform = "facebook" | "instagram" | "tiktok" | "youtube";
export type SocialFormat =
  | "feed_link" | "feed_image" | "carousel" | "reel" | "story"
  | "video" | "photo_post" | "short" | "long_video";
/**
 * `awaiting_media` — copy finished, the video it has to be posted on does not
 * exist yet. Mirrors `SocialVariantStatus` in keywords/src/social/types.ts.
 */
/** Mirrors SocialVideoJob in apps/ui-server/src/lib/social-video-jobs.ts. */
/** One of the video angles an article was found to contain, extracted at write time. */
export interface VideoTopicView {
  id: string;
  title: string;
  archetype: string;
  sections: number[];
  angle?: string;
}

/**
 * A planned reel, and the two gates around it.
 *
 * `draft` → `approved` (a person read the shot table; this unlocks spending)
 * → `filmed` → `released` (a person watched the reel; this unlocks publishing).
 */
/** ── The Video page ───────────────────────────────────────────────────────── */

export interface VideoQueueRow {
  keywordId: string | null;
  sanityId: string | null;
  label: string;
  /** Posts that cannot be sent until a clip exists. */
  waiting: number;
  formats: string[];
  hasTopics: boolean;
  storyboard: { id: number; status: string; topicTitle: string | null } | null;
  next: "extract-topics" | "plan" | "approve" | "film" | "watch" | "schedule";
  estimatedCostUsd: number;
  /** False for a Sanity-sourced pack: no path can film it. `blocked` says why. */
  runnable: boolean;
  blocked: string | null;
  /** The end-to-end run walking this row, if one is. */
  run: { id: number; status: VideoRunStatus; step: VideoRunStep } | null;
}

export type VideoRunStatus = "running" | "waiting_gate1" | "waiting_gate2" | "done" | "error" | "cancelled";
export type VideoRunStep = "topics" | "plan" | "gate1" | "film" | "gate2" | "schedule";

export interface VideoRunRowView {
  id: number;
  keywordId: string;
  label: string;
  storyboardId: number | null;
  status: VideoRunStatus;
  step: VideoRunStep;
  error: string | null;
  spentUsd: number;
  budgetCapUsd: number | null;
  batchId: string | null;
  estimatedTotalUsd: number;
  createdAt: string;
  updatedAt: string;
}

export interface VideoQueueView {
  queue: VideoQueueRow[];
  totals: {
    articles: number; posts: number; estimatedCostUsd: number;
    /** The half a run can actually act on; the rest have no keyword row. */
    runnableArticles: number; runnablePosts: number; runnableCostUsd: number;
  };
}

export interface VideoRunsView {
  planning: (StoryboardPlanJobView & { label: string })[];
  filming: {
    /** `interrupted` = the process was replaced mid-render, usually by a deploy. */
    keywordId: string; label: string; status: "rendering" | "done" | "error" | "interrupted";
    stage: string | null; stageDetail?: string | null; startedAt: string; finishedAt: string | null;
    error: string | null; warnings: string[];
  }[];
  recent: {
    storyboardId: number; keywordId: string; label: string; status: string; topicTitle: string | null;
    filmedAt: string | null; releasedAt: string | null; renderSeconds: number | null; spentUsd: number;
  }[];
  /**
   * Every storyboard that wants a decision from a person, newest-urgent first:
   * `filmed` (watch and release), `draft` (read and approve), `approved`
   * (nothing is filming it yet). Not keyed off runs — a board planned from the
   * article or approved by hand has no run row and still waits.
   */
  waiting: {
    storyboardId: number; keywordId: string; label: string; status: string;
    topicTitle: string | null; warningCount: number; costUsd: number;
    hasContactSheet: boolean; createdAt: string; updatedAt: string;
  }[];
  /** End-to-end runs, newest first. Includes the ones parked at a gate. */
  runs: VideoRunRowView[];
}

export interface VideoLibraryView {
  clips: {
    id: number; source_keyword_id: string | null; source_reel: string | null; role: string | null;
    subject: string; setting: string | null; time_of_day: string | null; mood: string | null;
    duration_sec: number | null; sanity_url: string | null; drive_view_url: string | null;
    cost_usd: number | null; times_used: number | null; last_used_at: string | null;
    retired: number | null; created_at: string;
  }[];
  reuse: { probes: number; wouldHaveMatched: number };
}

export interface VideoReelsView {
  reels: {
    storyboardId: number; keywordId: string; label: string; status: string; topicTitle: string | null;
    assetUrl: string | null; driveUrl: string | null; renderSeconds: number | null;
    filmedAt: string | null; releasedAt: string | null;
    posts: { platform: string; format: string; status: string; scheduled_for: string | null; postizReleaseUrl?: string | null }[];
  }[];
}

export interface StoryboardPlanJobView {
  id: string;
  keywordId: string;
  status: "running" | "done" | "error";
  startedAt: string;
  finishedAt: string | null;
  storyboardId: number | null;
  costUsd: number;
  error: string | null;
}

export interface StoryboardView {
  /** The finished reel on the CDN, once filmed. Null until then. */
  assetUrl?: string | null;
  /** Every shot of the finished reel as one image. See migration 089. */
  contactSheetUrl?: string | null;
  contactSheetAt?: string | null;
  id: number;
  keywordId: string;
  topicId: string | null;
  topicTitle: string | null;
  shotCount: number;
  shotSeconds: number;
  script: {
    casting: string;
    subjectState?: string | null;
    hookCategory?: string;
    totalWords: number;
    estimatedCostUsd: number;
    warnings: string[];
    shots: {
      index: number;
      seconds: number;
      mode: string;
      sourceSection?: number | null;
      spec: {
        subject: string; action: string; environment: string; light: string;
        cameraMove: string; shotSize: string;
      };
      tags: { role: string; subject: string; setting: string; timeOfDay: string; mood: string };
    }[];
    narration: { shotIndex: number; text: string; words: number }[];
  };
  warningCount: number;
  totalWords: number;
  status: "draft" | "approved" | "filmed" | "released" | "rejected";
  approvedAt: string | null;
  approvedBy: { id: string; email: string } | null;
  releasedAt: string | null;
  releasedBy: { id: string; email: string } | null;
  rejectedReason: string | null;
  filmedAt: string | null;
  assetId: number | null;
  renderSeconds: number | null;
  costUsd: number;
  createdAt: string;
}

/** A post living in Postiz that the platform did not create. */
export interface PostizPostView {
  id: string;
  content: string;
  state: "QUEUE" | "PUBLISHED" | "ERROR" | "DRAFT" | string;
  publishDate: string | null;
  releaseUrl: string | null;
  provider: string | null;
  channel: string | null;
}

export interface SocialVideoJob {
  keywordId: string;
  status: "rendering" | "done" | "error" | "idle";
  stage: string | null;
  /** Qualifies the stage, e.g. "shot 2 of 3" while footage is being billed. */
  stageDetail?: string | null;
  startedAt?: string;
  finishedAt?: string | null;
  error?: string | null;
  summary?: { slides?: number; durationSec?: number; bytes?: number; variantsFreed?: number } | null;
  warnings?: string[];
}

/** One post on the calendar. Times are ISO instants; render them in `timezone`. */
export interface VoiceLessonView {
  id: number;
  rule: string;
  why: string | null;
  status: "proposed" | "approved" | "rejected";
  evidenceEditIds: number[];
  createdAt: string;
  decidedAt: string | null;
}

export interface CopyEditView {
  id: number;
  platform: SocialPlatform;
  format: SocialFormat;
  source: "edit" | "postiz" | "steer";
  steer: string | null;
  beforeBody: string;
  afterBody: string;
  createdAt: string;
  distilledAt: string | null;
}

export interface BlackoutWindowView {
  startIso: string;
  endIso: string;
  kind: "shabbat" | "yomtov" | "memorial";
  labelHe: string;
  days: string[];
}

export interface HebrewDayView {
  date: string;
  hebrewDate: string;
  holidays: string[];
}

export interface BlackoutConflictView {
  variantId: number;
  label: string | null;
  platform: SocialPlatform;
  format: string;
  status: string;
  scheduledFor: string;
  inPostiz: boolean;
  window: Pick<BlackoutWindowView, "labelHe" | "kind" | "startIso" | "endIso">;
  suggested: string;
}

export interface GreetingView {
  key: string;
  labelHe: string;
  tier: 1 | 2;
  holidayDate: string;
  postAtIso: string;
  packId: string | null;
  draftsOn: string;
}

export interface PlannerPickView {
  id: number;
  week_start: string;
  candidate_id: string;
  label: string | null;
  url: string | null;
  slot_iso: string;
  seasonal_key: string | null;
  score: number;
  why_he: string;
  alternatives: { candidateId: string; label: string; score: number }[];
  decision: "pending" | "approved" | "replaced" | "removed";
  realized_at: string | null;
  realize_note: string | null;
}

export interface PlannerWeekSummaryView {
  weekStart: string;
  need: number;
  picked: number;
  seasonalGaps: string[];
  unplaced: string[];
}

export interface PlannerProposalView {
  id: string;
  created_at: string;
  trigger: "weekly" | "on-demand";
  until_iso: string;
  level: number;
  notes: { weeks: PlannerWeekSummaryView[]; postizError: string | null } | null;
  picks: PlannerPickView[];
}

export interface PlannerStateView {
  proposal: PlannerProposalView | null;
  metrics: { level: number; windowDays: number; proposals: number; decided: number; approved: number; replaced: number; removed: number; approvalRate: number | null; readyForNextLevel: boolean };
  perWeek: number;
  formats: string[];
  job: BackfillJobView | null;
}

export interface RadarMomentView {
  key: string;
  labelHe: string;
  from: string;
  to: string;
  daysUntil: number;
  writeBy: string;
  phase: "write" | "reshare" | "now";
  angles: string[];
  articles: { title: string; url: string | null; impressions28d: number | null; backfillId: string; backfillSource: "platform" | "sanity-blog"; matched: string }[];
  keywords: { id: string; keyword: string; volume: number | null; matched: string }[];
  gap: boolean;
}

export interface SeasonalIssueView {
  term: string;
  labelsHe: string[];
  date: string;
}

export interface CalendarReviewView {
  variantId: number;
  label: string | null;
  platform: SocialPlatform;
  format: string;
  status: string;
  scheduledFor: string;
  kind: "timing" | "somber";
  why: string;
  issues?: SeasonalIssueView[];
}

export interface BlackoutResolveJobView {
  id: string;
  status: "running" | "done";
  startedAt: string;
  finishedAt: string | null;
  total: number;
  results: { variantId: number; ok: boolean; from?: string; to?: string; note?: string }[];
}

export interface CalendarSlotView {
  id: number;
  packId: string;
  keywordId: string | null;
  label: string | null;
  campaign: string;
  platform: SocialPlatform;
  format: SocialFormat;
  status: SocialVariantStatus;
  scheduledFor: string;
  title: string | null;
  /** The truncated first line, for the card. */
  hook: string;
  /** The whole caption and its tags, for editing in place. */
  body: string;
  hashtags: string[];
  /** The call-to-action line, appended after the body when sent. */
  ctaText: string | null;
  /** The caption exactly as Postiz receives it, rendered by the server. */
  caption: string;
  /** The live post's URL, once Postiz reports it published. */
  releaseUrl: string | null;
  /** What the Postiz reconcile last changed on this post, and when. */
  syncNote: string | null;
  syncedAt: string | null;
  /** False when the copy broke a platform rule; it cannot be approved. */
  checksOk: boolean;
  hasMedia: boolean;
  /**
   * The Postiz post this became, or null. Non-null means moving or editing it
   * withdraws and re-creates it there, and that there is somewhere to link to.
   */
  postizPostId: string | null;
  /** Instagram Reels only: whether this goes out as a Trial Reel. */
  trialReel: boolean;
  clickCount: number;
  /** Frames the post carries: a story's 4 and a carousel's 8 look alike in Postiz. */
  slideCount: number;
  slideRoles: string[];
  /** What kind of deck it is and whether it is thin. Null for non-deck posts. */
  deck: DeckProfileView | null;
  /** The last send error, if any. */
  error: string | null;
}

export type DeckGoalKey = "list" | "explain" | "myths" | "guide" | "compare" | "expect";

export interface DeckProfileView {
  goal: DeckGoalKey;
  goalLabelHe: string;
  total: number;
  content: number;
  thin: boolean;
  reasonsHe: string[];
  suggestedContent: number;
}

export interface ThinDeckRowView {
  id: number;
  packId: string;
  label: string | null;
  platform: SocialPlatform;
  format: SocialFormat;
  status: SocialVariantStatus;
  scheduledFor: string | null;
  inPostiz: boolean;
  hook: string;
  profile: DeckProfileView;
  /** Content slides a free re-cut from the carousel would give; null if none. */
  recutContent: number | null;
}

export interface ThinDecksView {
  rows: ThinDeckRowView[];
  goals: { goal: DeckGoalKey; labelHe: string; whenHe: string }[];
  minContent: number;
  maxContent: number;
}

export interface BackfillCandidateView {
  keywordId: string;
  label: string;
  publishedAt: string | null;
  hasPack: boolean;
  /** -1 for a Sanity document: unknown until the preview fetches it. */
  formats: number;
  /** Set when the article cannot be backfilled, with the reason. */
  blocked?: string;
  /** The live page, for Sanity sources. */
  url?: string;
  /** Which list it came from. Set on every row of the mixed (`all`) listing. */
  source?: BackfillSourceView;
}

/** The lists the fill can draw from. `all` is the three content ones together. */
export type BackfillSourceView = "platform" | "sanity-blog" | "sanity-treatment" | "spotlight";

export interface BackfillPreviewView {
  dryRun: true;
  plan: {
    slots: { keywordId: string; platform: SocialPlatform; format: SocialFormat; scheduledFor: string; moved: boolean }[];
    perArticle: { keywordId: string; label: string; startsAt: string; posts: number; moved?: boolean; pinned?: boolean }[];
    moved: number;
    busyDays: { platform: SocialPlatform; day: string; count: number }[];
    lastSlotAt: string | null;
  };
  needCompletion: { keywordId: string; label: string; packId: string; formats: string[] }[];
  needGeneration: { keywordId: string; label: string }[];
  removedByHand: RemovedFormatView[];
  estimatedCostUsd: number;
  skipped: { keywordId: string; reason: string }[];
}

/**
 * A format the fill was asked for that somebody removed from the calendar by
 * hand. It is not re-created and not re-dated — that decision stands — but it is
 * reported, because a fill that silently produced nothing reads as a broken
 * button. `variantId` is what "החזרה ליומן" acts on.
 */
export interface RemovedFormatView {
  keywordId: string;
  label: string;
  variantId: number;
  platform: string;
  format: string;
  removedAt: string;
}

/** A rewrite running in the background. See `regenerate-jobs.ts` on the server. */
export interface RegenerateJobView {
  id: string;
  variantId: number;
  status: "running" | "done" | "error";
  startedAt: string;
  finishedAt: string | null;
  outcome: number | null;
  error: string | null;
  issues: SocialCheckIssue[];
  costUsd: number;
  postiz: { ok: true; postId: string | null; withdrew: string | null } | { ok: false; error: string } | null;
}

/** A pack being re-written or completed in the background. */
export interface PackRefreshJobView {
  id: string;
  packId: string;
  mode: "complete" | "rewrite";
  status: "running" | "done" | "error";
  startedAt: string;
  finishedAt: string | null;
  outcome: { ok: boolean; changed: string[]; rejected: { role: string; reason: string }[]; costUsd: number; error?: string } | null;
  error: string | null;
}

/** One read-back of Postiz into the platform. See `postiz-reconcile.ts`. */
export interface PostizSyncView {
  ranAt: string;
  skipped?: "throttled" | "not-configured";
  error: string | null;
  checked: number;
  published: number;
  errored: number;
  datesAdopted: number;
  textAdopted: number;
  deleted: number;
  changes: { id: number; what: string }[];
}

export interface BackfillJobView {
  id: string;
  status: "running" | "done" | "error";
  startedAt: string;
  finishedAt: string | null;
  total: number;
  done: number;
  current: string | null;
  phase: "generating" | "scheduling" | null;
  error: string | null;
  result: Omit<BackfillResultView, "dryRun"> | null;
}

export interface BackfillResultView {
  dryRun: false;
  ok: boolean;
  generated: number;
  /** Formats added to packs that already existed. */
  completed: number;
  rescheduled: number;
  costUsd: number;
  skipped: { keywordId: string; reason: string }[];
  /** Asked for, not placed, because a person had removed it. See `RemovedFormatView`. */
  removedByHand: RemovedFormatView[];
  errors: { keywordId: string; error: string }[];
  lastSlotAt: string | null;
}

export type SocialVariantStatus =
  | "draft" | "awaiting_media" | "approved" | "scheduled" | "posted" | "failed" | "skipped";

/** Per-post Postiz provider settings — today the Instagram Trial Reel flag. */
export type PostizVariantSettings = Record<string, unknown> | null;

export interface SocialSlideView {
  role: string;
  kicker: string;
  title: string | null;
  body: string | null;
  stats: { value: string; unit: string }[] | null;
  items: string[] | null;
  badge: string | null;
  assetId: number | null;
}

export interface SocialCheckIssue {
  rule: string;
  severity: "error" | "warn";
  message: string;
  field?: string;
}

export interface SocialPackView {
  id: string;
  keywordId: string | null;
  subjectKind: string;
  label: string | null;
  language: string;
  campaign: string;
  targetUrl: string | null;
  status: string;
  modelUsed: string | null;
  /** Roles and formats the subject could not fill, with the reason. */
  dropped: { role: string; reason: string }[];
  createdAt: string;
  updatedAt: string;
}

export interface SocialVariantView {
  id: number;
  packId: string;
  platform: SocialPlatform;
  format: SocialFormat;
  language: string;
  title: string | null;
  body: string;
  hashtags: string[];
  ctaText: string | null;
  /** Null on Instagram and TikTok, where a caption URL is not clickable. */
  linkCode: string | null;
  slides: SocialSlideView[];
  mediaAssetIds: number[];
  scale: Record<string, number | string> | null;
  /** Per-post Postiz provider settings; `is_trial_reel` is the one we set. */
  postizSettings: PostizVariantSettings;
  checks: SocialCheckIssue[];
  checksOk: boolean;
  status: SocialVariantStatus;
  scheduledFor: string | null;
  postizPostId: string | null;
  postizIntegrationId: string | null;
  /** When it went live (from Postiz), or when it was handed to Postiz while still scheduled. */
  postedAt: string | null;
  /** The live post's URL, once Postiz reports it published. */
  postizReleaseUrl: string | null;
  postizSyncNote: string | null;
  postizSyncedAt: string | null;
  error: string | null;
  clickCount: number;
  lastClickAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SocialVariantPatch {
  title?: string | null;
  body?: string;
  hashtags?: string[];
  ctaText?: string | null;
  slides?: SocialSlideView[];
  mediaAssetIds?: number[];
  scale?: Record<string, number | string> | null;
  scheduledFor?: string | null;
  status?: SocialVariantStatus;
  /** Instagram Reels only: send as a Trial Reel (non-followers first). */
  trialReel?: boolean;
}

export interface GenerateSocialPackResponse {
  ok: boolean;
  packId: string;
  deck: number;
  accepted: string[];
  rejected: { platform: SocialPlatform; issues: SocialCheckIssue[] }[];
  warnings: { platform: SocialPlatform; issues: SocialCheckIssue[] }[];
  deckError?: string;
  costUsd: number;
  attempts: number;
}

export interface SpotlightTherapistsResponse {
  items: SpotlightTherapist[]; total: number; eligibleCount: number; blockedCount: number;
  /** False when the roster was fetched without THERAPISTS_API_TOKEN — socials are unknown, not absent. */
  socialsVisible: boolean;
  reshareCount: number;
  byKind: Record<string, number>;
}
export interface SpotlightPayload {
  name: string; firstName: string; tagline: string | null; heading: string;
  quote: string | null; photoUrl: string | null; methods: string[]; cities: string[];
  languages: string[]; ageGroups: string[]; service: string[];
  sessionDuration: number | null; priceMin: number | null; priceMax: number | null;
}

export interface MethodSupplyItem {
  methodSlug: string;
  nameHe: string | null;
  therapistCount: number;
  withPhoto: number;
  topCities: { citySlug: string; cityHe: string | null; count: number }[];
  clusterId: string | null;
  supplyGate: number | null;
  phase: number | null;
  passes: boolean | null;
  wouldWake: boolean;
}
export interface MethodSupplyResponse {
  items: MethodSupplyItem[];
  total: number;
  lastSyncedAt: string | null;
  lastRun: { ran_at: string; source: string; therapists_total: number; ok: number; error: string | null } | null;
  gates: { total: number; wouldWake: number; alreadyLive: number; blocked: number; cityClusters: number };
  unmatched: { clusterId: string; nameHe: string; methodSlug: string | null }[];
}

export interface ExploreAnchorItem {
  methodSlug: string;
  url: string;
  treatmentName: string;
  anchor: string;
  source: "auto" | "operator";
  seeded: boolean;
}

export type AuditSeverityView = "high" | "medium" | "low";
export type AuditSourceView = "mirror" | "sitemap" | "crawl" | "all";

export interface AuditRunView {
  id: number;
  source: AuditSourceView;
  startedAt: string;
  finishedAt: string | null;
  status: "running" | "done" | "error";
  pagesCrawled: number;
  pagesTotal: number;
  findingsTotal: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
  error: string | null;
}

export interface AuditFindingView {
  id: number;
  runId: number;
  url: string;
  type: string;
  severity: AuditSeverityView;
  message: string;
  detail?: Record<string, unknown>;
  createdAt: string;
}

export interface AuditJobView {
  status: "idle" | "running" | "done" | "error";
  source?: AuditSourceView;
  runId?: number | null;
  crawled?: number;
  total?: number;
  startedAt?: string;
  finishedAt?: string | null;
  error?: string | null;
  summary?: { findingsTotal: number; high: number; medium: number; low: number; pagesCrawled: number } | null;
}

export interface ViralJobView {
  keywordId: string;
  status: "idle" | "rendering" | "done" | "error";
  stage?: "scene images" | "scene videos" | "voiceovers" | "composing" | "uploading" | null;
  mode?: "rerender" | "regenerate";
  startedAt?: string;
  finishedAt?: string | null;
  error?: string | null;
  summary?: { scenes?: number; totalSec?: number; bytes?: number; costEstimateUsd?: number } | null;
  warnings?: string[];
}

export interface ProduceJobView {
  keywordId: string;
  status: "idle" | "producing" | "done" | "error";
  total?: number;
  done?: number;
  currentHeading?: string | null;
  startedAt?: string;
  finishedAt?: string | null;
  error?: string | null;
  summary?: { totalWordCount?: number; estimatedCostUsd?: number | null; modelUsed?: string } | null;
}

export interface ImageJobView {
  keywordId: string;
  status: "idle" | "generating" | "done" | "error";
  mode?: "all" | "selective" | "missing";
  startedAt?: string;
  finishedAt?: string | null;
  error?: string | null;
  summary?: { generated: number; skipped: number; warnings: string[] } | null;
}

async function streamProduce(
  id: string,
  body: { authorId?: string; model?: string; withMeta?: boolean; allowDuplicate?: boolean },
  h: {
    onStart?: (d: ProduceStartEvent) => void;
    onSection?: (d: ProduceSectionEvent) => void;
    onDone?: (d: ProduceDoneEvent) => void;
    onError?: (error: string) => void;
    onDuplicate?: (info: DuplicateInfo, message: string) => void;
  },
  signal: AbortSignal,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`/api/pipeline/keywords/${encodeURIComponent(id)}/produce`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify(body),
      signal,
    });
  } catch (e) {
    if (signal.aborted) return;
    h.onError?.((e as Error).message);
    return;
  }
  if (!res.ok || !res.body) {
    // The duplicate gate refuses BEFORE the stream opens, as JSON. Without this
    // branch the operator saw "HTTP 409 Conflict" and no reason — which is not
    // meaningfully better than the silence that let the duplicate through.
    if (res.status === 409) {
      try {
        const payload = (await res.json()) as { code?: string; error?: string; duplicate?: DuplicateInfo };
        if (payload.code === "duplicate_coverage" && payload.duplicate) {
          h.onDuplicate?.(payload.duplicate, payload.error ?? "A live page already covers this keyword.");
          return;
        }
        h.onError?.(payload.error ?? `HTTP ${res.status} ${res.statusText}`);
        return;
      } catch {
        // fall through to the status line
      }
    }
    h.onError?.(`HTTP ${res.status} ${res.statusText}`);
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    // SSE frames are separated by \n\n.
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) >= 0) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      handleFrame(frame, h);
    }
  }
}

function handleFrame(
  frame: string,
  h: {
    onStart?: (d: ProduceStartEvent) => void;
    onSection?: (d: ProduceSectionEvent) => void;
    onDone?: (d: ProduceDoneEvent) => void;
    onError?: (error: string) => void;
  },
): void {
  let event = "message";
  let dataLine = "";
  for (const line of frame.split("\n")) {
    if (line.startsWith("event: ")) event = line.slice(7).trim();
    else if (line.startsWith("data: ")) dataLine += line.slice(6);
  }
  if (!dataLine) return;
  let payload: unknown;
  try {
    payload = JSON.parse(dataLine);
  } catch {
    return;
  }
  switch (event) {
    case "start":
      h.onStart?.(payload as ProduceStartEvent);
      break;
    case "section_done":
      h.onSection?.(payload as ProduceSectionEvent);
      break;
    case "done":
      h.onDone?.(payload as ProduceDoneEvent);
      break;
    case "error":
      h.onError?.((payload as { error: string }).error);
      break;
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Pipeline types — mirror the ui-server response shapes.
// ───────────────────────────────────────────────────────────────────────────

export type Language = "he" | "en";
export type Priority = "low" | "medium" | "high" | "urgent";
export type Status =
  | "backlog"
  | "researching"
  | "briefed"
  | "drafting"
  | "reviewing"
  | "published"
  | "distributed"
  | "archived"
  | "rejected";

export interface ClusterView {
  id: string;
  nameHe: string;
  nameEn: string;
  pillarKeywordHe: string | null;
  pillarKeywordEn: string | null;
  pillarStatus: string;
  funnelStages: string[];
  targetArticleCount: number | null;
  businessPriority: Priority;
  currentArticleCount: number;
  relatedClusters: string[];
}

export interface SeoScoreCheckView {
  id: string; label: string; status: "pass" | "warn" | "fail";
  earned: number; max: number; detail: string; fix?: string;
}
export interface SeoScoreView {
  score: number;
  grade: "excellent" | "good" | "needs-work" | "poor";
  checks: SeoScoreCheckView[];
  wordCount: number;
  headTermDensityPct: number;
  exactPhraseCount: number;
  failures: string[];
  warnings: string[];
}

export interface AuthorRecordView {
  id: string;
  sanityRef: string | null;
  nameHe: string;
  nameEn: string | null;
  bioHe: string | null;
  bioEn: string | null;
  credentialsHe: string | null;
  credentialsEn: string | null;
  profileUrl: string | null;
  avatarUrl: string | null;
  avatarAssetRef: string | null;
  slug: string | null;
}

export interface GuestPostInput {
  keyword: string;
  title: string;
  bodyMarkdown: string;
  language?: "he" | "en";
  clusterId?: string;
  withMeta?: boolean;
  author: { id: string } | { nameHe: string; nameEn?: string; bioHe: string; credentialsHe?: string; profileUrl?: string; avatarUrl?: string; avatarAssetRef?: string; slug?: string };
  heroImageAssetRef?: string;
  heroImageUrl?: string;
}

export type ClusterFamily = "MTH" | "LOC" | "COND" | "REM" | "NUT" | "WELL" | "LONG" | "PRO" | "BRAND";
export type ClusterIntentLabel = "T" | "C" | "I" | "B";

export interface CreateClusterInput {
  /** Slug id — auto-derived from nameEn server-side when omitted. */
  id?: string;
  nameHe: string;
  nameEn: string;
  pillarKeywordHe?: string | null;
  pillarKeywordEn?: string | null;
  businessPriority?: Priority;
  targetArticleCount?: number | null;
  description?: string | null;
  // Taxonomy placement so a new subcluster lands IN the tree.
  family?: ClusterFamily | null;
  subFamily?: string | null;
  intentLabel?: ClusterIntentLabel | null;
  pageType?: PlannerPageType | null;
  parentClusterId?: string | null;
  phase?: 1 | 2 | 3 | null;
  supplyGate?: number | null;
}

export interface EditClusterInput {
  nameHe?: string;
  nameEn?: string;
  description?: string | null;
  notes?: string | null;
  businessPriority?: Priority;
  intentLabel?: ClusterIntentLabel | null;
  pageType?: PlannerPageType | null;
  pillarKeywordHe?: string | null;
  pillarKeywordEn?: string | null;
  phase?: 1 | 2 | 3 | null;
  supplyGate?: number | null;
}

export interface KeywordSummary {
  id: string;
  keyword: string;
  language: Language;
  status: Status;
  /** Live, but the local pipeline version is newer than what's published → re-publish to push. */
  unpublishedChanges: boolean;
  /** Set when a LIVE page already serves this keyword (migration 043). */
  coveredByUrl?: string | null;
  /** A live page targets this exact phrase — the duplicate detector's verdict, per row. */
  duplicateOf?: { decision: "block" | "warn"; title: string; url: string; kind: string; basis: string } | null;
  priority: Priority;
  pillarPage: boolean;
  targetIntent: string | null;
  cluster: string | null;
  clusterId: string | null;
  hasSerp: boolean;
  hasBrief: boolean;
  hasArticle: boolean;
  hasArticleMarkdown: boolean;
  hasImages: boolean;
  hasVideo: boolean;
  /** Stored monthly search volume (from keyword_metrics), null if never looked up. */
  searchVolume: number | null;
  authorId: string | null;
  lastResearchedAt: string | null;
  lastReviewedAt: string | null;
  updatedAt: string;
  /** Per-action completion strip (research → brief → article → images → video → published → distributed). */
  stages: { key: string; label: string; done: boolean }[];
  /** Social posts of this article, skipped ones excluded. Null when it has none. */
  social?: { posted: number; scheduled: number; total: number } | null;
}

export interface KeywordResearchRow {
  keyword: string;
  languageCode: string;
  source: string;
  searchVolume: number | null;
  cpc: number | null;
  competitionLevel: string | null;
  keywordDifficulty: number | null;
  searchIntent: string | null;
  monthlySearches: { year: number; month: number; searchVolume: number }[] | null;
  fetchedAt: string;
  inPipeline: boolean;
  pipelineStatus: string | null;
  /** True when a LIVE article already targets this keyword (Content DB main keyword). */
  published: boolean;
}

export interface ResearchKeywordRow {
  id: string;
  keyword: string;
  language: Language;
  status: Status;
  clusterId: string | null;
  stages: { key: string; label: string; done: boolean }[];
  assetKinds: string[];
  searchVolume: number | null;
  keywordDifficulty: number | null;
  searchIntent: string | null;
  monthlySearches: { year: number; month: number; searchVolume: number }[] | null;
  opportunity: number;
  aiOverview: boolean | null;
  paaCount: number | null;
}

// ── Cluster Planner ──────────────────────────────────────────────────────────
export type PlannerFamilyId = "MTH" | "LOC" | "COND" | "REM" | "NUT" | "WELL" | "LONG" | "PRO" | "BRAND";
export type PlannerIntent = "T" | "C" | "I" | "B";
export type PlannerPageType = "hub" | "landing" | "editorial" | "comparison" | "faq_section" | "city_method" | "pro_article";

export interface PlannerFamilyMeta {
  id: PlannerFamilyId;
  nameHe: string;
  nameEn: string;
  money: boolean;
  description: string;
}

export interface PlannerClusterNode {
  id: string;
  nameHe: string;
  nameEn: string;
  family: PlannerFamilyId;
  subFamily: string | null;
  intentLabel: PlannerIntent | null;
  pageType: PlannerPageType | null;
  parentClusterId: string | null;
  pageUrl: string | null;
  phase: 1 | 2 | 3 | null;
  supplyGate: number | null;
  pillarStatus: string;
  businessPriority: string;
  relatedClusters: string[];
  description: string | null;
  keywordCount: number;
  pendingCount: number;
  rejectedCount: number;
  totalVolume: number;
  /** SEO opportunity score (reach lean): Σ volume × winnability × intent weight over the cluster's kept keywords. */
  opportunity: number;
  pipelineKeywordCount: number;
  action: PlannerActionKind;
  actionLabel: string | null;
  actionTone: "amber" | "brand" | "blue" | "emerald" | "muted";
  needsVolumes: boolean;
}

export type PlannerActionKind = "review" | "create-page" | "expand-page" | "live" | "planned" | "fold";

export type PlannerAuditKind = "cannibalization" | "pro-leak" | "orphan-spoke" | "dangling-ref" | "oversized" | "doorway-risk" | "supply-untracked";
export interface PlannerAuditFinding {
  kind: PlannerAuditKind;
  severity: "error" | "warning" | "info";
  clusterId: string;
  clusterNameHe: string;
  message: string;
  relatedIds?: string[];
}
export interface PlannerAuditReport {
  findings: PlannerAuditFinding[];
  counts: { error: number; warning: number; info: number };
  checkedClusters: number;
}

export interface PlannerTreeResponse {
  families: PlannerFamilyMeta[];
  counts: { pending: number; accepted: number; rejected: number };
  actionCounts: Record<PlannerActionKind, number>;
  clusters: PlannerClusterNode[];
}

export interface PlannerProposedCluster {
  idSuggestion: string;
  nameHe: string;
  nameEn: string;
  family: PlannerFamilyId;
  subFamily: string | null;
  pageType: PlannerPageType;
  parentClusterId: string | null;
  intentLabel: PlannerIntent;
  reason: string | null;
}

export interface PlannerActivityView {
  id: string;
  occurredAt: string;
  action: "accept" | "reject" | "reopen" | "reassign" | "set_primary";
  itemCount: number;
  items: { assignmentId: string; keyword: string; clusterId: string | null }[];
  summary: string;
  undone: boolean;
  undoneAt: string | null;
}

export interface PlannerAssignmentView {
  /** "assignment" = a pending suggestion; "keyword" = a cluster member (accepted/rejected tabs). */
  kind?: "assignment" | "keyword";
  id: string;
  keyword: string;
  language: "he" | "en";
  clusterId: string | null;
  clusterNameHe?: string | null;
  proposedNewCluster: PlannerProposedCluster | null;
  source: "deterministic" | "llm" | "manual" | "keyword";
  confidence: number | null;
  rationale: string | null;
  matchedSignal: string | null;
  isPrimary: boolean;
  status: "pending" | "accepted" | "rejected";
  searchVolume?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ClusterRollup {
  id: string | null;
  nameHe: string | null;
  nameEn: string;
  pillarStatus: string | null;
  keywordCount: number;
  totalVolume: number;
  publishedCount: number;
  withArticle: number;
  coveragePct: number;
  keywords: ResearchKeywordRow[];
}

export interface CoverageReport {
  publishedCount: number;
  cannibalizationCount: number;
  cannibalization: { keyword: string; urls: { title: string; url: string | null }[] }[];
  mapping: { id: string; keyword: string; status: Status; covered: boolean }[];
}

export type LinkEdgeKind = "contextual" | "related" | "treatment";

export interface LinkGraphNode {
  id: string;
  title: string;
  slug: string | null;
  url: string | null;
  clusterId: string | null;
  kind: "article" | "treatment";
  inContextual: number;
  inRelated: number;
  inTreatment: number;
  /** Of `inTreatment`, links inside the treatment page's text — these count as in-content. */
  inTreatmentBody: number;
  out: number;
}

export interface LinkGraph {
  nodes: LinkGraphNode[];
  edges: { from: string; to: string; kind: LinkEdgeKind }[];
  orphans: string[];
  contextualOrphans: string[];
  linksAvailable: boolean;
}

export interface LinkSuggestion {
  sanityId: string;
  title: string;
  url: string | null;
  keyword: string;
  clusterId: string | null;
  hasMainKeyword: boolean;
  /** False when the topic check had not finished (or failed) — candidates are word-match only. */
  topicChecked?: boolean;
  diagnostic?: string | null;
  candidates: {
    sanityId: string;
    title: string;
    url: string | null;
    /** Keyword-word match, 0–1 (0 for a page found only by topic). */
    score: number;
    kind: string;
    topic?: "strong" | "related" | "none";
    topicReason?: string;
    via?: "words" | "topic";
    /** Whether the run may write a NEW sentence here (an existing mention can always be wrapped). */
    mayGenerate?: boolean;
  }[];
}

/** Progress of the background "Implement backlinks" run (polled while running). */
export interface FixLinksStatus {
  status: "idle" | "running" | "done" | "error";
  id?: string;
  startedAt?: string;
  finishedAt?: string | null;
  total?: number;
  done?: number;
  current?: string | null;
  error?: string | null;
  results?: {
    url: string;
    title: string;
    linked: number;
    contextual: number;
    generated: number;
    considered?: number;
    diagnostic?: string;
    error?: string;
    /** What happened at each candidate host page, and why when nothing was written. */
    hosts?: {
      sanityId: string;
      title: string;
      score: number;
      kind: string;
      outcome: "linked" | "already-linked" | "skipped" | "not-reached";
      detail?: string;
      /** Topic verdict (strong / related / none) and why, when the topic check ran. */
      topic?: "strong" | "related" | "none";
      topicReason?: string;
      /** "topic" = found only by topic overlap, not by the keyword's words. */
      via?: "words" | "topic";
    }[];
  }[];
}

// ── Manual add + CSV import ────────────────────────────────────────────────
export interface AddKeywordInput {
  keyword: string;
  language: Language;
  clusterId?: string | null;
  priority?: Priority;
  pillarPage?: boolean;
  intent?: "informational" | "commercial" | "transactional" | "navigational" | null;
  primaryKeywordId?: string | null;
  notes?: string | null;
}

export interface PatchKeywordInput {
  keyword?: string;
  priority?: Priority;
  status?: Status;
  clusterId?: string | null;
  pillarPage?: boolean;
  targetIntent?: "informational" | "commercial" | "transactional" | "navigational" | null;
  notes?: string | null;
  rejectionReason?: string | null;
  primaryKeywordId?: string | null;
  manualSecondaryKeywords?: string[];
}

export type BulkKeywordAction = "archive" | "reject" | "set_priority" | "set_cluster" | "delete";

export interface BulkKeywordActionInput {
  ids: string[];
  action: BulkKeywordAction;
  priority?: Priority;
  clusterId?: string | null;
  reason?: string;
}

export interface BulkKeywordActionResponse {
  ok: boolean;
  action: BulkKeywordAction;
  succeeded: number;
  failed: number;
  results: { id: string; ok: boolean; error?: string }[];
}

export type ImportCanonicalField =
  | "keyword"
  | "language"
  | "volume"
  | "difficulty"
  | "cpc"
  | "intent"
  | "cluster"
  | "primary_keyword"
  | "related_keywords"
  | "url"
  | "ignore";

export interface ImportMapping {
  source: string;
  target: ImportCanonicalField;
  confidence: number;
  rationale: string | null;
}

export interface ImportPreviewResponse {
  preview: {
    headers: string[];
    rows: Record<string, string>[];
    totalRows: number;
  };
  mappings: ImportMapping[];
  mapperModel: string;
}

export interface CannibalizationGroupView {
  primary: string;
  secondaries: string[];
  rationale: string;
  confidence: number;
}

export interface CannibalizationResponse {
  totalKeywords: number;
  groups: CannibalizationGroupView[];
  standalone: string[];
  modelUsed: string;
}

export interface ImportCommitInput {
  csvContent: string;
  mappings: ImportMapping[];
  groups: { primary: string; secondaries: string[] }[];
  defaultClusterId?: string | null;
  defaultLanguage?: Language;
  defaultPriority?: Priority;
  /** Route still-unclustered keywords to their best-fit existing cluster via the classifier. */
  autoClassify?: boolean;
  classifyThreshold?: number;
  /** Operator-confirmed triage verdicts (keyed by the repaired keyword text). */
  triage?: TriageDecision[];
  /** People/orgs named in the keywords — queued into the Entity Hub for review. */
  entities?: { name: string; type: "individual" | "organization" | "product" | "platform"; role?: string }[];
}

export interface ImportCommitResponse {
  ok: boolean;
  inserted: number;
  skipped: number;
  primariesCreated: number;
  secondariesCreated: number;
  standaloneCreated: number;
  rejectedRows?: number;
  foldedRows?: number;
  entitiesQueued?: number;
  entitiesAlreadyKnown?: number;
  classified?: number;
  proposedNewClusters?: { id: string; nameEn: string; count: number }[];
  warnings: string[];
}

// ── Bulk-upload triage ─────────────────────────────────────────────────────

export type TriageVerdict = "keep" | "review" | "reject";

export interface TriageDecision {
  keyword: string;
  verdict: TriageVerdict;
  reason?: string;
  clusterId?: string | null;
  impact?: number;
}

export interface TriagedRow {
  keyword: string;
  volume: number | null;
  difficulty: number | null;
  verdict: TriageVerdict;
  reason: string;
  rationale: string;
  entities: { name: string; type: string; role: string }[];
  audience: "patient" | "practitioner" | "unknown";
  intent: string | null;
  businessFit: number;
  impact: number;
  variantOf: string | null;
  clusterId: string | null;
}

export interface ImportGapReportView {
  coverage: { keeps: number; clustered: number; unclustered: number; unclusteredPct: number };
  taxonomyGaps: { theme: string; token: string; count: number; totalVolume: number; examples: string[] }[];
  scopeAlerts: { reason: string; count: number; totalVolume: number; examples: string[]; message: string }[];
  dataQuality: { kind: string; count: number; examples: string[]; message: string }[];
  headline: string[];
}

export interface ImportTriageResponse {
  ok: boolean;
  total: number;
  counts: { keep: number; review: number; reject: number };
  modelUsed: string;
  llmCalls: number;
  rows: TriagedRow[];
  entities: { name: string; type: string; role: string }[];
  gaps: ImportGapReportView;
  error?: string;
}

export interface KeywordFilter {
  clusterId?: string;
  language?: Language;
  status?: Status[];
  q?: string;
}

export interface RelatedKeywordRef {
  id: string;
  keyword: string;
  language: Language;
  cluster: string | null;
  status: Status;
}

export interface KeywordDetail {
  keyword: {
    id: string;
    keyword: string;
    language: Language;
    status: Status;
    priority: Priority;
    pillarPage: boolean;
    targetIntent: string | null;
    cluster: string | null;
    clusterId: string | null;
    notes: string | null;
    sourceTags: string[];
    manualSecondaryKeywords: string[];
    authorId: string | null;
    lastReviewedAt: string | null;
    firstPublishedAt: string | null;
    serp: unknown;
    trends: unknown;
    brief: unknown;
    article: unknown;
    articleMarkdown: string | null;
    producedAt: string | null;
    rejectionReason: string | null;
    createdAt: string;
    updatedAt: string;
    lastResearchedAt: string | null;
    serpDiff: unknown;
    primaryKeywordId: string | null;
    importedMeta: Record<string, unknown> | null;
    storyboard: unknown;
    storyboardGeneratedAt: string | null;
  };
  primary: RelatedKeywordRef | null;
  secondaries: RelatedKeywordRef[];
  extracts: ExtractSummary[];
  /** Do-not-mention entities named in the brief's article-bound fields (H2/must-cover/FAQ/sources). */
  doNotMention: { name: string; count: number; context: string; reason: string | null }[];
  cluster: { id: string; nameHe: string; nameEn: string; pillarStatus: string; businessPriority: Priority } | null;
  /** Whether this article's Sanity doc is already live in the Content DB (drives the re-publish guard) + whether the local version has unpublished changes. */
  live: { isLive: boolean; url: string | null; unpublishedChanges: boolean };
}

export interface ExtractSummary {
  id: number | undefined;
  url: string;
  title: string | null;
  publisher: string | null;
  wordCount: number | null;
  h1Count: number | null;
  h2Count: number | null;
  h3Count: number | null;
  status: string;
  fetchedAt: string;
}

export interface ActionResult {
  ok: boolean;
  summary?: string;
  warnings?: string[];
  error?: string;
  [k: string]: unknown;
}

export type SceneTransitionView = "cut" | "fade" | "slide-left" | "slide-up";
export type SceneImageMotionView = "static" | "subtle-zoom" | "pan-left" | "pan-right" | "ken-burns";

export interface EditableScene {
  index: number;
  durationSec: number;
  imagePrompt: string;
  hebrewCaption: string;
  narration: string;
  transition: SceneTransitionView;
  imageMotion: SceneImageMotionView;
  visualNote: string | null;
}

export interface EditableStoryboard {
  totalSec: number;
  aspectRatio: "9:16" | "1:1" | "16:9";
  h1: string;
  hook: string;
  cta: string | null;
  scenes: EditableScene[];
  musicCue: string | null;
  brandWatermark: string | null;
  generatedAt: string;
  modelUsed: string;
  estimatedCostUsd: number | null;
}

export type KeywordAssetKind =
  | "hero" | "in_content" | "social_card" | "thumbnail"
  | "short_vertical" | "square_feed" | "long_horizontal"
  | "article_markdown"
  | "voiceover_long" | "voiceover_scene"
  | "scene_image" | "scene_video" | "viral_final";

export type KeywordAssetStatus = "pending" | "uploaded" | "partial" | "failed" | "skipped";

export interface BackupRecordView {
  filename: string;
  path: string;
  bytes: number;
  createdAt: string;
  ageDays: number;
}

export interface BackupStatusResponse {
  ok: boolean;
  status: {
    enabled: boolean;
    destDir: string;
    retainCount: number;
    count: number;
    totalBytes: number;
    newest: BackupRecordView | null;
    oldest: BackupRecordView | null;
  };
  records: BackupRecordView[];
  scheduler: {
    enabled: boolean;
    cronExpression: string;
    nextRunAt: string | null;
    inFlight: boolean;
    lastRunAt: string | null;
    lastResult: {
      ok: boolean;
      filename?: string;
      bytes?: number;
      prunedCount: number;
      durationMs: number;
      error?: string;
    } | null;
    totalRuns: number;
  };
}

export type CostProvider =
  | "anthropic" | "bedrock" | "elevenlabs" | "replicate" | "openai"
  | "serpapi" | "firecrawl" | "dataforseo" | "sanity" | "google-drive" | "other";

export interface CostLedgerView {
  id: number;
  occurredAt: string;
  provider: CostProvider;
  operation: string;
  keywordId: string | null;
  model: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  cacheReadTokens: number | null;
  cacheCreationTokens: number | null;
  chars: number | null;
  units: number | null;
  estimatedCostUsd: number;
  source: string | null;
  notes: string | null;
}

export interface CostSummaryBreakdown<K extends string> {
  key: K;
  totalUsd: number;
  rows: number;
}

export interface CostSummaryResponse {
  ok: boolean;
  range: { since: string; until: string };
  totalUsd: number;
  rowCount: number;
  byProvider: CostSummaryBreakdown<CostProvider>[];
  byOperation: CostSummaryBreakdown<string>[];
}

export interface CostTimeseriesPoint {
  bucket: string;
  totalUsd: number;
  rowCount: number;
}

export interface CostTimeseriesResponse {
  ok: boolean;
  range: { since: string; until: string };
  bucket: "day" | "hour";
  points: CostTimeseriesPoint[];
}

export type PipelineErrorLevel = "error" | "warning" | "info";

export interface PipelineErrorView {
  id: number;
  occurredAt: string;
  operation: string;
  level: PipelineErrorLevel;
  keywordId: string | null;
  clusterId: string | null;
  message: string;
  stack: string | null;
  context: Record<string, unknown> | null;
  resolved: boolean;
  resolvedAt: string | null;
  resolvedBy: string | null;
  resolvedNote: string | null;
  /** Ready-to-paste Claude prompt to diagnose + fix this error (server-generated). */
  suggestedPrompt?: string;
}

export interface ErrorListFilters {
  unresolvedOnly?: boolean;
  level?: PipelineErrorLevel;
  operation?: string;
  operationPrefix?: string;
  keywordId?: string;
  since?: string;
  limit?: number;
}

export interface KeywordAssetView {
  id: number;
  kind: KeywordAssetKind;
  sectionIndex: number | null;
  filename: string;
  localPath: string;
  mimeType: string | null;
  byteSize: number | null;
  sanityUrl: string | null;
  driveViewUrl: string | null;
  notes: string | null;
  prompt: string | null;
  status: KeywordAssetStatus;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── Dashboard responses ───────────────────────────────────────────────────
export interface OverviewResponse {
  kpis: {
    totalKeywords: number;
    totalClusters: number;
    briefsReady: number;
    articlesProduced: number;
    articlesPublished: number;
    distributed: number;
    spend30dUsd: number;
    spendSparkline: number[];
  };
  funnel: Record<Status, number>;
  recentActivity: ActivityEvent[];
  recentArticles: RecentArticle[];
  clusters: {
    id: string;
    nameEn: string;
    nameHe: string;
    pillarStatus: string;
    businessPriority: Priority;
    currentArticleCount: number;
    targetArticleCount: number | null;
  }[];
}

export interface ActivityEvent {
  ts: string;
  keywordId: string;
  keyword: string;
  kind: "enriched" | "briefed" | "produced" | "refreshed";
}

export interface RecentArticle {
  keywordId: string;
  keyword: string;
  language: Language;
  h1: string;
  wordCount: number;
  estimatedCostUsd: number | null;
  generatedAt: string;
  cluster: string | null;
  lastReviewedAt: string | null;
}

/** One validated step in an AI resolution plan. Nothing here is applied until you approve it. */
export interface PlannedFix {
  keywordId: string;
  keyword: string;
  findingCode: string;
  action: "fold_secondaries" | "re_enrich" | "no_action";
  siblings: { id: string; keyword: string }[];
  reason: string;
  confidence: "high" | "medium" | "low";
  preview: string;
}

/** A proposal awaiting your decision. Persisted server-side; holds production for its keywords. */
export interface StoredPlan {
  id: number;
  createdAt: string;
  status: "pending" | "applied" | "discarded";
  model: string | null;
  costUsd: number | null;
  excluded: number;
  rejected: string[];
  fixes: (PlannedFix & { fixId: number; approved: boolean; outcome: string | null })[];
}

export interface EnrichQueueJobView {
  id: string;
  status: "running" | "done" | "error";
  startedAt: string;
  finishedAt: string | null;
  total: number;
  done: number;
  current: string | null;
  error: string | null;
  estimatedCostUsd: number;
  items: {
    rank: number;
    keywordId: string;
    keyword: string;
    outcome: "enriched" | "already-enriched" | "no-competitors" | "error";
    competitors?: number;
    detail?: string;
  }[];
}

export interface SerpApiQuotaView {
  keys: (
    | {
        key: "SERPAPI_KEY" | "ALT_SERPAPI_KEY";
        ok: true;
        planName: string | null;
        searchesPerMonth: number | null;
        searchesLeft: number;
        thisMonthUsage: number | null;
      }
    | { key: "SERPAPI_KEY" | "ALT_SERPAPI_KEY"; ok: false; error: { code: string; message: string } }
  )[];
  totalSearchesLeft: number;
  enrichesLeft: number;
  checkedAt: string;
}

export interface AiAssignOutcome {
  keywordId: string;
  keyword: string;
  outcome: "assigned" | "unchanged" | "low-confidence" | "new-cluster-suggested" | "no-fit" | "error";
  previousClusterId: string | null;
  clusterId: string | null;
  clusterNameHe: string | null;
  clusterNameEn: string | null;
  confidence: number | null;
  rationale: string | null;
  suggestedNewCluster: { idSuggestion: string; nameHe: string; nameEn: string; reason: string } | null;
  error?: string;
}

/** One line per outcome kind, for a banner after an AI assignment. */
export function summariseAiAssign(results: AiAssignOutcome[]): string {
  const n = (o: AiAssignOutcome["outcome"]) => results.filter((r) => r.outcome === o).length;
  const parts = [
    `${n("assigned")} assigned`,
    n("unchanged") && `${n("unchanged")} already in the right cluster`,
    n("low-confidence") && `${n("low-confidence")} left as-is (low confidence)`,
    n("new-cluster-suggested") && `${n("new-cluster-suggested")} need a new cluster`,
    n("no-fit") && `${n("no-fit")} fit no cluster`,
    n("error") && `${n("error")} failed`,
  ].filter(Boolean);
  const one = results.length === 1 ? results[0] : null;
  if (one) {
    const where = one.clusterNameHe || one.clusterNameEn;
    if (one.outcome === "assigned") return `“${one.keyword}” → ${where} (${Math.round((one.confidence ?? 0) * 100)}%). ${one.rationale ?? ""}`;
    if (one.outcome === "new-cluster-suggested" && one.suggestedNewCluster)
      return `“${one.keyword}” fits no existing cluster — suggested new cluster “${one.suggestedNewCluster.nameHe}” (${one.suggestedNewCluster.nameEn}). Create it from the dropdown. ${one.rationale ?? ""}`;
    if (one.outcome === "low-confidence")
      return `“${one.keyword}” — best guess ${where} at ${Math.round((one.confidence ?? 0) * 100)}%, not applied. ${one.rationale ?? ""}`;
    if (one.outcome === "error") return `“${one.keyword}” — AI assignment failed: ${one.error}`;
    return `“${one.keyword}” — ${one.outcome}. ${one.rationale ?? ""}`;
  }
  return `AI assignment: ${parts.join(" · ")}`;
}

export interface PipelineTask {
  id: string;
  keywordId: string;
  keyword: string;
  language: Language;
  clusterId: string | null;
  cluster: string | null;
  category:
    | "needs_enrich"
    | "needs_scrape"
    | "needs_brief"
    | "ready_to_produce"
    | "ready_to_distribute"
    | "needs_refresh"
    // Not derived from the keyword's own state like the others — this one says
    // "the autopilot is about to write this and something is wrong with it".
    | "preflight";
  title: string;
  description: string;
  priority: Priority;
  hint: string;
  /** Pre-flight tasks only — the findings behind the row, shown inside it. */
  findings?: {
    code: string;
    severity: "block" | "warn" | "note";
    summary: string;
    detail: string | null;
    fix: string | null;
    source: "auto" | "model";
    /** What closes it — drives which button the row offers. */
    resolution: "auto" | "apply" | "enrich" | "decide" | "info";
    /** Fold/split findings carry the sibling rows, so the row can apply them. */
    data?: { siblings?: { id: string; keyword: string; reason: string }[] } | null;
    /** The model assessed this and chose not to act — its reasoning, shown in place. */
    aiVerdict?: string | null;
  }[];
}

export interface TasksResponse {
  tasks: PipelineTask[];
  byCategory: Record<string, PipelineTask[]>;
  /** TRUE per-category counts (byCategory arrays are capped for rendering). */
  totals?: Record<string, number>;
  total: number;
  /** Per-category render cap; when totals[c] > cap, the list is truncated. */
  cap?: number;
}

// ── Articles ───────────────────────────────────────────────────────────────
export interface ArticleFilter {
  language?: Language;
  cluster?: string;
  q?: string;
  sort?: "newest" | "oldest" | "stalest" | "longest" | "shortest";
  hasMeta?: boolean;
}

export interface ArticleRowView {
  keywordId: string;
  keyword: string;
  language: Language;
  cluster: string | null;
  clusterId: string | null;
  status: string;
  pillarPage: boolean;
  authorId: string | null;
  h1: string;
  metaTitle: string | null;
  metaDescription: string | null;
  slug: string | null;
  totalWordCount: number;
  sectionCount: number;
  estimatedCostUsd: number | null;
  modelUsed: string;
  hasRelatedLinks: boolean;
  hasAuthor: boolean;
  generatedAt: string;
  lastReviewedAt: string | null;
  warnings: string[];
}

export interface ArticlesResponse {
  count: number;
  stats: {
    totalArticles: number;
    hebrew: number;
    english: number;
    pillars: number;
    totalCostUsd: number;
    avgWordCount: number;
  };
  articles: ArticleRowView[];
}

// ── Audience ───────────────────────────────────────────────────────────────
export interface AudienceListView {
  id: number;
  name: string;
  reportedSubscribers: number;
  actualSubscribers: number;
  totalBlacklisted: number;
  folderId?: number;
  isMaster: boolean;
}

export interface AudienceOverviewResponse {
  kpis: {
    totalContacts: number;
    totalLists: number;
    recentBlacklisted: number;
    recentOrphans: number;
    growthSparkline12mo: number[];
  };
  lists: AudienceListView[];
}

export interface AudienceContactRow {
  id: number;
  email: string;
  blacklisted: boolean;
  listIds: number[];
  createdAt?: string;
  modifiedAt?: string;
  firstName: string | null;
  lastName: string | null;
  sms: string | null;
}

export interface AudienceContactsResponse {
  count: number;
  limit: number;
  offset: number;
  listId: number | null;
  contacts: AudienceContactRow[];
}

// ── Discovery / Sources ────────────────────────────────────────────────────
export type DiscoveryStatusView = "new" | "triaged" | "promoted" | "ignored" | "stale";
export type DiscoveryKindView = "url_analysis" | "rss_item" | "crawl_find" | "youtube_video";

export interface YoutubeExtrasView {
  videoId: string;
  channelName: string | null;
  channelId: string | null;
  durationSeconds: number | null;
  viewCount: number | null;
  likeCount: number | null;
  thumbnailUrl: string | null;
  transcriptLanguage: string | null;
  transcriptWordCount: number | null;
  transcriptExcerpt: string | null;
  purpose: string | null;
  hook: string | null;
  keyTakeaways: string[];
  suggestedOutline: { h2: string; summary: string | null }[];
  audience: string | null;
}

export type DiscoverySourceKindView = "rss" | "sitemap" | "youtube_channel";

export interface RssFeedView {
  id: string;
  name: string;
  url: string;
  kind: DiscoverySourceKindView;
  entityId: string | null;
  youtubeChannelId: string | null;
  defaultClusterId: string | null;
  language: "he" | "en" | "other";
  pollIntervalMinutes: number;
  autoAnalyze: boolean;
  active: boolean;
  lastFetchedAt: string | null;
  lastFetchSucceeded: boolean;
  lastFetchError: string | null;
  lastItemCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface RssFeedInputView {
  id: string;
  name: string;
  url: string;
  kind?: DiscoverySourceKindView;
  defaultClusterId?: string | null;
  language?: "he" | "en" | "other";
  pollIntervalMinutes?: number;
  autoAnalyze?: boolean;
  active?: boolean;
}

export interface DiscoveryAnalysisView {
  h1: string | null;
  h2s: string[];
  h3s: string[];
  wordCount: number | null;
  schemaTypes: string[];
  entities: string[];
  inferredKeyword: string | null;
  inferredClusterId: string | null;
  matchedKeywordId: string | null;
  gapAnalysis: string | null;
  recommendation: string | null;
  analyzedOk: boolean;
  analyzedAt: string | null;
  llmModel: string | null;
  youtube: YoutubeExtrasView | null;
}

export interface DiscoveryView {
  id: number;
  kind: DiscoveryKindView;
  sourceId: string | null;
  url: string;
  title: string | null;
  excerpt: string | null;
  publisher: string | null;
  language: string | null;
  publishedAt: string | null;
  analysis: DiscoveryAnalysisView;
  status: DiscoveryStatusView;
  promotedToKeywordId: string | null;
  notes: string | null;
  discoveredAt: string;
  triagedAt: string | null;
  updatedAt: string;
}

export interface DiscoveryItemFilter {
  status?: DiscoveryStatusView;
  kind?: DiscoveryKindView;
  sourceId?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface PollFeedResultView {
  feedId: string;
  ok: boolean;
  error?: string;
  itemsSeen: number;
  itemsNew: number;
  itemsAnalyzed: number;
  discoveries: DiscoveryView[];
  fetchedAt: string;
}

// ── Scheduler status ─────────────────────────────────────────────────────
export interface SweepEntryView {
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  feedsPolled: number;
  itemsSeen: number;
  itemsNew: number;
  itemsAnalyzed: number;
  failedFeedIds: string[];
  results: { feedId: string; ok: boolean; itemsNew: number; itemsSeen: number; error?: string }[];
}

export interface SchedulerStatusResponse {
  scheduler: {
    enabled: boolean;
    reason: string | null;
    cronExpression: string;
    nextRunAt: string | null;
    inFlight: boolean;
    lastSweep: SweepEntryView | null;
    totalSweeps: number;
    recentSweeps: SweepEntryView[];
  };
  health: {
    totalSources: number;
    activeSources: number;
    pausedSources: number;
    failingSources: number;
  };
}

export interface PromoteDiscoveryInput {
  keyword: string;
  language: Language;
  clusterId?: string | null;
  priority?: Priority;
  pillarPage?: boolean;
  intent?: "informational" | "commercial" | "transactional" | "navigational" | null;
  notes?: string | null;
  /** True when promoted despite a "skip" recommendation — tagged for audit. */
  override?: boolean;
}

// ── Distribution log ──────────────────────────────────────────────────────
export type DistributionChannelView = "telegram" | "brevo" | "whatsapp" | "meta" | "x" | "youtube" | "tiktok" | "postiz";
export type DistributionKindView = "test" | "transactional" | "campaign" | "channel_post";
export type DistributionStatusView = "sent" | "scheduled" | "failed";

export interface DistributionRowView {
  id: number;
  keywordId: string | null;
  channel: DistributionChannelView;
  kind: DistributionKindView;
  status: DistributionStatusView;
  messageId: string | null;
  campaignId: string | null;
  chatId: string | null;
  listId: number | null;
  recipientEmail: string | null;
  recipientsCount: number;
  subject: string | null;
  assetType: "text" | "photo" | "video" | null;
  assetPath: string | null;
  url: string | null;
  htmlBytes: number | null;
  scheduledAt: string | null;
  sentAt: string;
  error: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  keyword: { id: string; keyword: string; language: Language; cluster: string | null } | null;
}

export interface DistributionFilter {
  channel?: DistributionChannelView;
  status?: DistributionStatusView;
  keywordId?: string;
  limit?: number;
}

export interface DistributionOverviewResponse {
  kpis: {
    totalSends: number;
    last30dSends: number;
    successCount: number;
    scheduledCount: number;
    failedCount: number;
    byChannel: Record<string, number>;
    sparkline30d: number[];
  };
}

// ── Chat ──────────────────────────────────────────────────────────────────
export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ChatToolCallEvent {
  id: string;
  name: string;
  input: Record<string, unknown>;
}
export interface ChatToolResultEvent {
  id: string;
  name: string;
  ok: boolean;
  summary: string;
}

export function streamChat(
  body: { messages: ChatMessage[]; page?: string; focusedKeywordId?: string },
  handlers: {
    onStart?: () => void;
    onToken?: (text: string) => void;
    onToolCall?: (call: ChatToolCallEvent) => void;
    onToolResult?: (result: ChatToolResultEvent) => void;
    onDone?: () => void;
    onError?: (msg: string) => void;
  },
): () => void {
  const controller = new AbortController();
  void (async () => {
    let res: Response;
    try {
      res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
    } catch (e) {
      if (controller.signal.aborted) return;
      handlers.onError?.((e as Error).message);
      return;
    }
    if (!res.ok || !res.body) {
      handlers.onError?.(`HTTP ${res.status} ${res.statusText}`);
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) return;
      buffer += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buffer.indexOf("\n\n")) >= 0) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        let event = "message";
        let data = "";
        for (const line of frame.split("\n")) {
          if (line.startsWith("event: ")) event = line.slice(7).trim();
          else if (line.startsWith("data: ")) data += line.slice(6);
        }
        if (!data) continue;
        let payload: unknown;
        try { payload = JSON.parse(data); } catch { continue; }
        if (event === "start") handlers.onStart?.();
        else if (event === "token") handlers.onToken?.((payload as { text: string }).text);
        else if (event === "tool_call") handlers.onToolCall?.(payload as ChatToolCallEvent);
        else if (event === "tool_result") handlers.onToolResult?.(payload as ChatToolResultEvent);
        else if (event === "done") handlers.onDone?.();
        else if (event === "error") handlers.onError?.((payload as { error: string }).error);
      }
    }
  })();
  return () => controller.abort();
}

// SSE event payloads
export interface ProduceStartEvent {
  keywordId: string;
  h1: string;
  totalSections: number;
}
export interface ProduceSectionEvent {
  index: number;
  total: number;
  level: "intro" | "h2" | "faq" | "conclusion";
  heading: string | null;
  wordCount: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
}
export interface ProduceDoneEvent {
  totalWordCount: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCacheReadTokens: number;
  totalCacheCreationTokens: number;
  estimatedCostUsd: number | null;
  modelUsed: string;
  warnings: string[];
  meta: { title: string; description: string; slug: string } | null;
}

// ── Cluster expansion ──────────────────────────────────────────────────────

export type SuggestionStatus = "pending" | "accepted" | "rejected" | "duplicate";
export type SuggestionSource =
  | "dfs_related"
  | "dfs_suggestions"
  | "llm_expansion"
  | "paa_mining"
  | "trends_rising";
export type FunnelStage = "TOFU" | "MOFU" | "BOFU";

// ── Coverage detection (do we already have a live page for this keyword?) ────

export type CoverageVerdict = "covered" | "partial" | "gap";

/** Why a page matched — see `content-db/coverage.ts`. Drives wording and the gate. */
export type CoverageMatchBasis = "operator-keyword" | "title" | "title-tokens" | "topic-overlap";

export interface CoverageMatch {
  sanityId: string;
  title: string;
  url: string;
  kind: PublishedKind;
  score: number;
  titleMatch: boolean;
  basis: CoverageMatchBasis;
}

/** The live page a blocked produce/brief collided with. */
export interface SplitProposalView {
  id: string;
  clusterId: string;
  clusterName: string | null;
  status: "proposed" | "applied" | "discarded";
  inputCount: number;
  subClusterCount: number;
  validationOk: boolean;
  validation: { ok: boolean; problems: string[] };
  repair: unknown | null;
  output: {
    sub_clusters: { name_he: string; name_en: string; primary_keyword: string; intent: string; keywords: string[]; rationale: string }[];
    notes?: string;
  };
  inputKeywords: { keyword: string; volume: number }[];
  estimatedCostUsd: number | null;
  createdAt: string;
  appliedAt: string | null;
}

export interface SplitApplyView {
  parentClusterId: string;
  created: { clusterId: string; nameHe: string; keywords: number; primary: string }[];
  moved: number;
  drift: { goneFromCluster: string[]; newSinceProposal: string[] };
  collisions: { proposed: string; usedBy: string; renamedTo: string }[];
  dryRun: boolean;
}

export interface DuplicateInfo {
  /** The live page's Sanity id — what "Merge into this article" points the keyword at. */
  sanityId?: string | null;
  title: string | null;
  url: string | null;
  kind: PublishedKind | null;
  basis: CoverageMatchBasis | null;
  score: number | null;
  gsc: { query: string; position: number } | null;
}

export interface CoverageResult {
  verdict: CoverageVerdict;
  score: number;
  best: CoverageMatch | null;
  alternatives: CoverageMatch[];
  recommendation: string;
  /** A duplicate on the evidence, or close enough that a human must look first. */
  suspectedDuplicate: boolean;
}

export interface SuggestionView {
  id: string;
  clusterId: string;
  keyword: string;
  language: Language;
  source: SuggestionSource;
  sourceSeed: string | null;
  rationale: string | null;
  estimatedIntent: string | null;
  recommendedPriority: Priority | null;
  funnelStage: FunnelStage | null;
  isPillarCandidate: boolean;
  searchVolume: number | null;
  cpc: number | null;
  competition: string | null;
  keywordDifficulty: number | null;
  status: SuggestionStatus;
  reviewedAt: string | null;
  reviewedNotes: string | null;
  acceptedKeywordId: string | null;
  suggestedClusterId: string | null;
  suggestedNewCluster: { idSuggestion: string; nameHe: string; nameEn: string; reason: string } | null;
  classifierConfidence: number | null;
  classifierRationale: string | null;
  /** Coverage against the live Sanity corpus — attached by the suggestions endpoint. */
  coverage?: CoverageResult;
  createdAt: string;
  updatedAt: string;
}

export interface ClusterProposalView {
  idSuggestion: string;
  nameHe: string;
  nameEn: string;
  reasons: string[];
  suggestionIds: string[];
  keywords: string[];
  averageConfidence: number;
}

export interface ApproveClusterProposalInput {
  id?: string;
  nameHe?: string;
  nameEn?: string;
  description?: string | null;
  businessPriority?: Priority;
  acceptSuggestionIds?: string[];
}

// ── Content database (live Sanity mirror) ────────────────────────────────

export type PublishedKind = "article" | "treatment_category" | "treatment_subcategory";

export interface PublishedArticleView {
  sanityId: string;
  sanityType: string;
  kind: PublishedKind;
  title: string;
  slug: string;
  url: string;
  urlOverride: string | null;
  urlOverrideNote: string | null;
  language: Language;
  excerpt: string | null;
  metaDescription: string | null;
  topicTags: string[];
  derivedTopics: string[];
  mainKeyword: string | null;
  secondaryKeywords: string[];
  clusterId: string | null;
  clusterConfidence: number | null;
  clusterRationale: string | null;
  parentSanityId: string | null;
  publishedAt: string | null;
  sanityUpdatedAt: string;
  lastSyncedAt: string;
  isLive: boolean;
  gscImpressions28d: number | null;
  gscClicks28d: number | null;
  gscCtr28d: number | null;
  gscAvgPosition28d: number | null;
  gscTopQueries: { query: string; impressions: number; clicks: number; position: number }[] | null;
  gscLastSyncedAt: string | null;
}

export interface PublishedSyncJobView {
  status: "idle" | "running" | "done" | "error";
  phase?: "fetching" | "processing" | "done";
  current?: number;
  total?: number;
  error?: string | null;
  result?: PublishedSyncResponse | null;
}

export interface PublishedSyncResponse {
  ok: boolean;
  summary: string;
  fetchedBlogPosts: number;
  fetchedParentCategories: number;
  fetchedSubcategories: number;
  inserted: number;
  refreshed: number;
  unchanged: number;
  classifierCalls: number;
  topicExtractorCalls: number;
  llmCostEstimateUsd: number;
  skipped: { sanityId: string; reason: string }[];
  newlyStale: number;
  durationMs: number;
  warnings: string[];
}

export interface WeaveLinksResponse {
  ok: boolean;
  bodyMarkdown: string;
  inserted: { anchor: string; url: string; relationship: string; mustHave: boolean }[];
  mode: "woven" | "fallback";
  summary: string;
  error?: string;
}

export interface ReconcileStatusResponse {
  ok: boolean;
  summary: string;
  count: number;
  statusReconciled: number;
  datesStamped: number;
  keywordsLinked: number;
  dryRun: boolean;
  reconciled: {
    keywordId: string;
    keyword: string;
    from: string;
    to: string;
    statusChanged: boolean;
    lastPublishedAtStamped: string | null;
    mirrorKeywordLinked: string | null;
    docId: string;
    liveUrl: string;
  }[];
  error?: string;
}

export interface ClassifyUnclusteredResponse {
  ok: boolean;
  summary: string;
  totalUnclustered: number;
  considered: number;
  assigned: number;
  skipped: number;
  failed: number;
  dryRun: boolean;
  costEstimateUsd: number;
  rows: {
    sanityId: string;
    title: string;
    kind: string;
    clusterId: string | null;
    clusterName: string | null;
    confidence: number | null;
    outcome: "assigned" | "low_confidence" | "no_fit" | "failed";
    note?: string;
  }[];
}

export interface ExpandClusterInput {
  desiredCount?: number;
  maxDfsSeeds?: number;
  dfsDepth?: number;
  dfsLimit?: number;
  skipDfs?: boolean;
  model?: string;
}

export interface ExpandClusterResponse {
  ok: boolean;
  summary: string;
  inserted: number;
  refreshed: number;
  dfsCost: { account: number; task: number };
  dfsRawCount: number;
  dfsCandidateCount: number;
  gapsFilled: string;
  llm: {
    modelUsed: string;
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens: number;
    cacheCreationTokens: number;
  };
  classifier: {
    suggestionsClassified: number;
    crossClusterFits: number;
    newClusterProposals: number;
    inputTokens: number;
    outputTokens: number;
    errors: number;
  } | null;
  warnings: string[];
  suggestions: SuggestionView[];
}

export interface AcceptSuggestionInput {
  keyword?: string;
  language?: Language;
  priority?: Priority;
  primaryKeywordId?: string | null;
  notes?: string | null;
  /** Override destination cluster (lets operator accept into a different cluster than the suggestion's origin). */
  targetClusterId?: string;
}

// ── Brand kit ────────────────────────────────────────────────────────────────

export type BrandFileKind = "md" | "json" | "yaml" | "text";

export interface BrandFileEntry {
  path: string;
  filename: string;
  ext: string;
  kind: BrandFileKind;
  sizeBytes: number;
  content: string;
  parsedJson?: unknown;
  jsonParseError?: string;
}

export interface BrandSectionEntry {
  name: string;
  files: BrandFileEntry[];
}

export interface BrandAssetEntry {
  name: string;
  kind: "image" | "font" | "document" | "other";
  sizeBytes: number;
  ext: string;
  url: string;
}

export interface BrandManifest {
  brandRoot: string | null;
  available: boolean;
  sections: BrandSectionEntry[];
  assets: BrandAssetEntry[];
  topLevelFiles: BrandFileEntry[];
}

// ── Search + notifications shapes ─────────────────────────────────────────────
export interface SearchHit {
  id: string;
  title: string;
  subtitle?: string;
}
export interface SearchGroup {
  type: "keyword" | "article" | "cluster" | "entity";
  label: string;
  route: string;
  items: SearchHit[];
}
export interface SearchResponse {
  query: string;
  groups: SearchGroup[];
}

export interface NotificationItem {
  id: string;
  kind: "error" | "warning" | "activity" | "task";
  title: string;
  subtitle?: string;
  ts: string | null;
  route: string;
}
export interface NotificationsResponse {
  items: NotificationItem[];
  latestTs: string | null;
}

// ── AWS-billed Bedrock cost (Cost Explorer) ───────────────────────────────────
export interface AwsBedrockDailyCost {
  date: string;
  amount: number;
}
export interface AwsBedrockCostResponse {
  ok: boolean;
  currency: string;
  region: string;
  days: number;
  total: number;
  monthToDate: number;
  daily: AwsBedrockDailyCost[];
  lastUpdated: string;
  error?: string;
}

/** One inbound link a backlink pass wrote, read live from Sanity. */
export interface BacklinkPlacement {
  id: string;
  hostId: string;
  hostTitle: string;
  hostType: string;
  hostUrl: string | null;
  hostHasDraft: boolean;
  targetUrl: string;
  targetSlug: string;
  targetTitle: string | null;
  targetKeyword: string | null;
  /** What the link text should be: the main keyword, else the target's title without its subtitle. */
  linkPhrase: string;
  /** body-sentence = a generated sentence; body-mention = the author's own text, wrapped; faq = a generated FAQ answer. */
  kind: "body-sentence" | "body-standalone" | "body-mention" | "faq";
  anchor: string;
  sentence: string;
  context: string;
  faqQuestion?: string;
  editable: boolean;
  /** Why it cannot be edited here (the sentence also carries another link). */
  blockedReason?: string;
  editMode: "append" | "standalone" | "in-place" | "faq";
  /** Placements on other pages with a near-identical sentence. */
  duplicateOf?: string[];
  /** Smallest correct fix, no AI: the current sentence with only the link phrase swapped; offered only when it passes every check. */
  minimalFix?: string | null;
  flags: string[];
  createdAt?: string | null;
  createdBy?: string | null;
  editedAt?: string | null;
  /** What the pass originally wrote, before the first operator edit. */
  originalSentence?: string | null;
  /** Marked fine by the operator (for exactly this sentence). */
  approved: boolean;
  /** The link text the operator chose for this target in an earlier edit. */
  learnedPhrase: string | null;
  /** Has an operator edit that can be undone (restores the previous sentence; nothing is learned from it). */
  undoable: boolean;
  /** The relevance check's verdict (host page → target article), when it has run. */
  relevance?: { verdict: "strong" | "related" | "none"; reason: string | null; checkedAt: string } | null;
}

/** A general rule distilled from the operator's link-sentence edits. */
export interface BacklinkLesson {
  id: number;
  rule: string;
  why: string | null;
  status: "proposed" | "approved" | "rejected";
  evidenceEventIds: number[];
  createdAt: string;
  decidedAt: string | null;
}

/** A link removed from the Backlinks page (restorable). */
export interface RemovedBacklink { eventId: number; removedAt: string; hostId: string; hostTitle: string | null; targetUrl: string; kind: string | null; anchor: string | null; sentence: string | null }

/** Progress of the background relevance check over every link. */
export interface BacklinkRelevanceJob { status: "running" | "done" | "error"; startedAt: string; finishedAt: string | null; targets: number; done: number; links: number; unrelated: number; failed: number; current: string | null; error: string | null }

// ── Source intake ──────────────────────────────────────────────────────────
export type SourceProposalStatus = "pending" | "approved" | "rejected";
export type SourceProposalOutcome = "new" | "join" | "method" | "extend";
export interface SourceProposalKeywordView {
  keyword: string;
  volume: number;
  kd: number | null;
  position: number | null;
  etv: number | null;
  existing?: { id: string; status: string; keyword: string; clusterId: string | null } | null;
}
export interface SourceProposalView {
  id: number;
  primaryKeyword: string;
  keywords: SourceProposalKeywordView[];
  sources: string[];
  competitorUrls: string[];
  discoveryId: number | null;
  demand: number;
  bestPosition: number | null;
  etv: number;
  maxKd: number | null;
  score: number;
  outcome: SourceProposalOutcome | "known" | "skip";
  detail: string | null;
  ourUrl: string | null;
  targetClusterId: string | null;
  relevance: "serve" | "adjacent" | "out" | null;
  relevanceReason: string | null;
  suggestedFamily: string | null;
  status: SourceProposalStatus;
  decisionNote: string | null;
  appliedKeywordId: string | null;
  decidedAt: string | null;
  createdAt: string;
}
export interface SourceIntakeStatusView {
  running: boolean;
  enabled: boolean;
  cron: string;
  nextRunAt: string | null;
  last: {
    at: string; trigger: string; ok: boolean; dryRun: boolean; topics: number; inserted: number; updated: number;
    costUsd: number; byOutcome: Record<string, number>; errors: string[];
    domains: { domain: string; rows: number; costUsd: number; error?: string }[];
  } | null;
}

export interface SerpApiLoggedUsageView {
  since: string | null;
  requests: number;
  billable: number;
  byKey: { key: string; billable: number }[];
  byCaller: { caller: string; billable: number }[];
}
